import { ArgumentScale, EventTracker } from "@devexpress/dx-react-chart";
import {
  ArgumentAxis,
  Chart,
  Tooltip as DXTooltip,
  Legend,
  LineSeries,
  Title,
  ValueAxis
} from "@devexpress/dx-react-chart-material-ui";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LaunchIcon from "@mui/icons-material/Launch";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LockResetIcon from "@mui/icons-material/LockReset";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Tooltip from "@mui/material/Tooltip";
import { Breadcrumbs } from "components/common/Breadcrumbs";
import { Breadcrumb } from "components/common/Breadcrumbs/types";
import { DatabasePasswordDialog } from "components/common/DatabasePasswordDialog";
import { FormDialog, selectOptionSchema } from "components/common/FormDialog";
import {
  FIELD_TYPES,
  FormDialogProps,
  SelectOption
} from "components/common/FormDialog/types";
import { Head } from "components/common/Head";
import { Loader } from "components/common/Loader";
import { Menu } from "components/common/Menu";
import { Table } from "components/common/Table";
import {
  TableColumn,
  TableRowActionsMenuItem
} from "components/common/Table/types";
import { scaleTime } from "d3-scale";
import { useMount } from "hooks/useMount";
import { usePrevious } from "hooks/usePrevious";
import { useUnmount } from "hooks/useUnmount";
import { databasesActions } from "modules/databases/slices";
import {
  DBServiceParamsSelector,
  backupFilesSelector,
  defaultEncoding,
  extensionCatalogueSelector,
  tableBackupSetsSelector,
  rawBackupSetsSelector,
  backupStatusSelector,
  databaseCustomOptionsSelector,
  customParameterSetting,
  databaseEncodingsSelector,
  databaseMetricsSelector,
  databaseResetPasswordSelector,
  databaseSelector,
  databaseUserSelector,
  accountsEditable,
  accountsReadOnlyReason,
  databasesEditable,
  poolerEditable,
  isCronBackupDeletingSelector,
  isCronBackupUpdatingSelector,
  isDatabaseBackingUpSelector,
  isDatabaseDeletingSelector,
  isDatabaseGettingManagementUrlSelector,
  isDatabaseRestoringSelector,
  isDatabaseRollingBackSelector,
  haMemberCount,
  isServing,
  versionSelectOptions,
  isDatabaseStatusChangingSelector,
  isDatabaseUpdatingSelector,
  restoreNeedsDatabase,
  supportsPerDatabaseBackups,
  supportsEncodings,
  supportsExtensions,
  supportsCustomParameters,
  supportsNamedDatabases,
  supportsPerDatabaseGrants,
  supportsReplicas,
  SERVER_WIDE_GRANT_SCOPE,
  tableDatabaseChildrenSelector,
  tableDatabaseUsersSelector,
  tableReplicaDatabasesSelector
} from "modules/databases/selectors";

import {
  CHANGE_STATUS_TYPES,
  DATABASES,
  DATABASE_BACKUP_STATUSES,
  DATABASE_REPLICA_STATUSES,
  DATABASE_RESTORE_PHASES,
  DATABASE_RESTORE_STATUSES,
  DATABASE_STATUSES,
  ExtensionSetting,
  DatabasePermission,
  ExtensionInfo,
  PoolerMode,
  TableBackupSet,
  TableDatabaseBackupFiles,
  TableDatabaseChild,
  TableDatabaseCustomOptions,
  TableDatabaseUser,
  TableReplicaDatabase,
  UpdateDatabaseChildParams
} from "modules/databases/types";
import {
  DATABASE_ERROR_MESSAGES,
  DATABASE_METRIC_DEFAULT_DURATION,
  DATABASE_METRIC_DEFAULT_RANGE,
  POOLER_DISCONNECT_WARNING,
  POOLER_MODE_DESCRIPTIONS,
  POOLER_MODE_OPTIONS
} from "modules/databases/constants";
import {
  ExtensionHost,
  availableExtensions,
  extensionDetails,
  extensionHost,
  extensionOptions,
  extensionSettings,
  extensionsInUse,
  installedExtensions,
  restartNote,
  selectedExtensionNames,
  normalizeSettingValue,
  settingValueError,
  strandedByUpgrade,
  upgradeWarning
} from "modules/databases/extensions";
import { migrationAlert } from "modules/databases/migration";
import {
  poolerDisconnectsClients,
  poolerUpdate
} from "modules/databases/pooler";
import {
  childDatabaseNameSchema,
  isParameterValue,
  userNameSchema
} from "modules/databases/validation";

import { Alert, AlertTitle, Box, Chip, ChipProps } from "@mui/material";
import { enterprisesActions } from "modules/enterprises/slices";
import { organizationSelector } from "modules/enterprises/selectors";
import { notificationsActions } from "modules/notifications/slices";
import { NOTIFICATION_TYPES } from "modules/notifications/types";
import { pollingActions } from "modules/polling/slices";
import { createPolling } from "modules/polling/utils";
import { projectsActions } from "modules/projects/slices";
import {
  organizationProjectsSelector,
  projectSelector
} from "modules/projects/selectors";
import { FC, Fragment, useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { generatePath, useNavigate, useParams } from "react-router-dom";
import { isNumber } from "typeGuards/isNumber";
import { isSelectOption } from "typeGuards/isSelectOption";
import { isString } from "typeGuards/isString";
import { isUndefined } from "typeGuards/isUndefined";
import { formatDate } from "utils/formatDate";
import { generateSearchString } from "utils/generateSearchString";
import { getParentPath } from "utils/getParentPath";
import { getSelectOption } from "utils/getSelectOption";
import { roundTo } from "utils/roundTo";
import { array, number, string } from "yup";
import {
  CLIENT_TIME_ZONE,
  DATE_FORMATS,
  MAX_DATABASE_REPLICAS_COUNT,
  MAX_PORT,
  MIN_PORT,
  ROUTES
} from "../../constants";
import {
  extensionSettingsFields,
  settingField,
  pickedExtensionSettingsFields,
  settingsPayload
} from "./extensionSettingsFields";
import * as s from "./styles";
import { DIALOG_TYPES, TABS } from "./types";
import { LoadBalancers } from "./LoadBalancers";
import { Notice, sortedNotices } from "./notices";
import { S3LinkDialog } from "./S3LinkDialog";
import { DatabaseBackups } from "./Backups";
import {
  backupFilesErrorSelector,
  isDatabaseS3LinkingSelector,
  isDatabaseS3UnlinkingSelector
} from "modules/databases/selectors";
import { BackupScheduleDialog } from "./BackupScheduleDialog";
import { backupScheduleResultSelector } from "modules/databases/selectors";

const POLL_ID_PREFIX = "DATABASE";

const POLL_IDS = {
  database: "DATABASE",
  databaseMetrics: "DATABASE_METRICS",
  firewallRules: "FIREWALL_RULES",
  databaseEncodings: "DATABASE_ENCODINGS",
  databaseChildren: "DATABASE_CHILDREN",
  databaseUsers: "DATABASE_USERS",
  databaseUser: "DATABASE_USER",
  replicaDatabases: "REPLICA_DATABASES",
  databaseBackupStatus: "DATABASE_BACKUP_STATUS",
  databaseBackupFiles: "DATABASE_BACKUP_FILES",
  databaseCustomOptions: "DATABASE_CUSTOM_OPTIONS"
};

const PERMISSION_OPTIONS = [
  { label: "Read", value: "R" },
  { label: "Read/Write", value: "RW" }
];

// The single permission field shown for an engine whose grants are server-wide.
const SERVER_WIDE_FIELD = "permission_server_wide";

const SERVER_WIDE_GRANT_NOTE =
  "ℹ️ Access applies to the whole instance, including every numbered database";

// An unanswered permission select reads as the narrower level
const readPermissionLevel = (value: unknown): "R" | "RW" =>
  typeof value === "object" &&
  value !== null &&
  "value" in value &&
  value.value === "RW"
    ? "RW"
    : "R";

const normalizeGrantLevel = (level?: string): "R" | "RW" =>
  ["RW", "READWRITE", "WRITE", "ALL"].includes(
    (level || "").trim().toUpperCase()
  )
    ? "RW"
    : "R";

const TAB_TITLES: { [key in TABS]: string } = {
  [TABS.DATABASES]: "Databases",
  [TABS.USERS]: "Users",
  [TABS.REPLICAS]: "Replicas",
  [TABS.BACKUPS]: "Backups",
  [TABS.CUSTOM_OPTIONS]: "Custom options",
  [TABS.NETWORK]: "Network"
};

const toOptionOrUndefined = (value?: string): SelectOption | undefined =>
  value ? { label: value, value } : undefined;

// Charset and collation exist only where supportsEncodings: MongoDB stores UTF-8 BSON and
// Redis has no named databases.
const databaseChildrenTableColumnsFor = (
  serviceName: string | undefined,
  hasExtensions: boolean,
  hasNotes: boolean
): TableColumn<TableDatabaseChild>[] => [
  { key: "name", label: "Name" },
  ...(supportsEncodings(serviceName)
    ? [
        { key: "charset" as const, label: "Charset" },
        { key: "collation" as const, label: "Collation" }
      ]
    : []),
  ...(hasExtensions
    ? [
        { key: "extensionsString" as const, label: "Extensions" },
        { key: "settingsString" as const, label: "Settings" }
      ]
    : []),
  ...(hasNotes ? [{ key: "notes" as const, label: "Notes" }] : [])
];

const EXTENSIONS_NOTE =
  "ℹ️ Extensions are created in this database by the platform. Removing one stops managing it and resets its settings, but does not uninstall it.";

const SETTINGS_NOTE =
  "ℹ️ Applied to this database without a restart. Leave a field empty to use the extension default.";

const extensionsNote =
  (
    catalogue: ExtensionInfo[],
    host: ExtensionHost,
    presentOnInstance: string[]
  ) =>
  (fieldValues: Record<string, unknown>) => {
    const selected = selectedExtensionNames(fieldValues.extensions);
    return [
      EXTENSIONS_NOTE,
      extensionDetails(catalogue, host, selected),
      restartNote(catalogue, selected, presentOnInstance)
    ]
      .filter(Boolean)
      .join("\n");
  };

const databaseUsersTableColumns: TableColumn<TableDatabaseUser>[] = [
  { key: "name", label: "Name" },
  {
    key: "permissions",
    label: "Permissions"
  }
];

const replicasTableColumns: TableColumn<TableReplicaDatabase>[] = [
  { key: "name", label: "Name" },
  { key: "service_status", label: "Status" },
  { key: "mode", label: "Mode" },
  { key: "versionString", label: "Version" },
  { key: "ageString", label: "Created" }
];

const customOptionsTableColumns: TableColumn<TableDatabaseCustomOptions>[] = [
  { key: "nameString", label: "Name" },
  { key: "unit", label: "Unit" },
  { key: "min_value", label: "Min value" },
  { key: "max_value", label: "Max value" },
  { key: "default_value", label: "Default value" },
  { key: "custom_value", label: "Custom value" },
  { key: "effective_value", label: "Effective value" },
  { key: "state", label: "State" }
];

// Maps a same-cluster HA/failover group phase (status.replicaStatus.phase) to a
// MUI chip color.
const getHAPhaseChipColor = (phase?: string): ChipProps["color"] => {
  switch (phase) {
    case "Healthy":
      return "success";
    case "Suspect":
    case "Degraded":
      return "warning";
    case "FailingOver":
    case "SwitchingOver":
      return "info";
    case "SplitBrainRisk":
      return "error";
    default:
      return "default";
  }
};

const databasePasswordTextAfterReset =
  "Please save the password for further use.";

type EditDatabaseFormData = {
  cpu: SelectOption;
  memory: SelectOption;
  disk: string;
  port: string;
  version: SelectOption;
  ha_enabled: boolean;
  pooler_enabled?: boolean;
  pooler_mode?: SelectOption;
};

const NOTICE_ICONS = {
  error: ErrorOutlineIcon,
  warning: WarningAmberIcon,
  info: InfoOutlinedIcon
} as const;

const REJECTED_ALERTS = [
  { key: "rejected_extensions", title: "Extensions not installed" },
  { key: "rejected_settings", title: "Settings not applied" },
  {
    key: "rejected_encodings",
    title: "Charset and collation not applied (server default used)"
  }
] as const;

export const Database: FC = () => {
  const dispatch = useDispatch();
  const matchParams = useParams<{
    organizationId: string;
    regionId: string;
    projectId: string;
    serviceName: string;
    databaseId: string;
  }>();
  const history = useNavigate();
  const organization = useSelector(organizationSelector);
  const project = useSelector(projectSelector);
  const projects = useSelector(organizationProjectsSelector);
  const database = useSelector(databaseSelector);
  const databaseMetrics = useSelector(databaseMetricsSelector);
  const backupStatus = useSelector(backupStatusSelector);
  const backupFilesError = useSelector(backupFilesErrorSelector);
  const storageLinking = useSelector(isDatabaseS3LinkingSelector);
  const storageUnlinking = useSelector(isDatabaseS3UnlinkingSelector);
  const databaseChildren = useSelector(tableDatabaseChildrenSelector);
  const databaseUsers = useSelector(tableDatabaseUsersSelector);
  const databaseUser = useSelector(databaseUserSelector);
  const backupFiles = useSelector(backupFilesSelector);
  const backupSets = useSelector(tableBackupSetsSelector);
  const rawBackupSets = useSelector(rawBackupSetsSelector);
  const [isStorageLinkOpened, setStorageLinkOpened] = useState(false);
  const [isScheduleOpened, setScheduleOpened] = useState(false);
  const scheduleResult = useSelector(backupScheduleResultSelector);
  const replicaDatabases = useSelector(tableReplicaDatabasesSelector);
  const databaseCustomOptions = useSelector(databaseCustomOptionsSelector);
  const databaseEncodings = useSelector(databaseEncodingsSelector);
  const DBServiceParams = useSelector(DBServiceParamsSelector);
  const extensionCatalogue = useSelector(extensionCatalogueSelector);
  const [isDatabasePasswordDialogOpened, setIsDatabasePasswordDialogOpened] =
    useState(false);
  const [
    isResetDefaultPasswordDialogOpened,
    setIsResetDefaultPasswordDialogOpened
  ] = useState(false);
  const [dialog, setDialog] = useState<{
    isOpened: boolean;
    type: DIALOG_TYPES;
  }>({ type: DIALOG_TYPES.EDIT, isOpened: false });
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);

  const databaseResetedPassword = useSelector(databaseResetPasswordSelector);
  const isDatabaseRestoring = useSelector(isDatabaseRestoringSelector);
  const isDatabaseRollingBack = useSelector(isDatabaseRollingBackSelector);
  const isDatabaseBackingUp = useSelector(isDatabaseBackingUpSelector);
  const isDatabaseUpdating = useSelector(isDatabaseUpdatingSelector);
  const isDatabaseDeleting = useSelector(isDatabaseDeletingSelector);
  const isDatabaseGettingManagementUrl = useSelector(
    isDatabaseGettingManagementUrlSelector
  );
  const isDatabaseStatusChanging = useSelector(
    isDatabaseStatusChangingSelector
  );

  const isCronBackupUpdating = useSelector(isCronBackupUpdatingSelector);
  const isCronBackupDeleting = useSelector(isCronBackupDeletingSelector);

  const [isActionsMenuOpened, setIsActionsMenuOpened] =
    useState<boolean>(false);
  const actionsMenuButtonRef = useRef<HTMLButtonElement | null>(null);
  const requestedTab = new URLSearchParams(location.search).get("tab");
  const activeTabIndexFromParam = Object.keys(TAB_TITLES).find(
    (key) =>
      TAB_TITLES[key] &&
      String(TAB_TITLES[key]).toLowerCase() ===
        (requestedTab === "scheduler" ? "backups" : requestedTab)
  );
  const [activeTabIndex, setActiveTabIndex] = useState<TABS>(
    Number(activeTabIndexFromParam || TABS.DATABASES)
  );
  const previousActiveTabIndex = usePrevious(activeTabIndex);

  const previousDatabaseId = usePrevious(matchParams.databaseId);

  const isOperationInProgress =
    isDatabaseUpdating ||
    isDatabaseDeleting ||
    isDatabaseGettingManagementUrl ||
    isDatabaseStatusChanging ||
    isCronBackupUpdating ||
    isCronBackupDeleting ||
    isDatabaseRestoring ||
    isDatabaseRollingBack ||
    isDatabaseBackingUp;
  const previousIsOperationInProgress = usePrevious(isOperationInProgress);

  const latestCronRunStatus =
    backupStatus?.cron_runs && backupStatus.cron_runs.length > 0
      ? backupStatus.cron_runs[backupStatus.cron_runs.length - 1].status
      : undefined;

  const backupOperationInProgress =
    isDatabaseRollingBack ||
    isDatabaseBackingUp ||
    isDatabaseRestoring ||
    ![DATABASE_RESTORE_STATUSES.COMPLETED, undefined].includes(
      backupStatus?.restore_status
    ) ||
    ![DATABASE_BACKUP_STATUSES.COMPLETED, undefined].includes(
      backupStatus?.backup_status
    ) ||
    ![DATABASE_BACKUP_STATUSES.COMPLETED, undefined].includes(
      latestCronRunStatus
    );
  const areBackupOrRestoreDisabled =
    backupOperationInProgress || !isServing(database?.service_status);

  const selectedReplicaDatabaseProjectId = replicaDatabases?.find(
    (pj) => pj.id === selectedItemId
  )?.project_id;

  const initializePermissions = useCallback(
    (userName: string) => {
      const user = databaseUsers?.find((u) => u.name === userName);
      if (user && user.permissions) {
        return user.permissions
          .split(", ")
          .filter((perm) => perm.includes(":")) // Ensure the permission string contains a colon
          .map((perm) => {
            const [db, permission] = perm.split(":");
            return {
              database: db,
              permission: normalizeGrantLevel(permission)
            };
          });
      }
      return [];
    },
    [databaseUsers]
  );

  useEffect(() => {
    if (selectedItemId) {
      initializePermissions(selectedItemId);
    }
  }, [selectedItemId, initializePermissions]);

  const isCreateReplicaEnabled =
    database &&
    [
      DATABASE_REPLICA_STATUSES.PRIMARY,
      DATABASE_REPLICA_STATUSES.STANDALONE
    ].includes(database?.mode) &&
    isServing(database?.service_status) &&
    (replicaDatabases?.length || 0) < MAX_DATABASE_REPLICAS_COUNT;

  const areAccountsEditable = accountsEditable(database);
  const accountsReason = accountsReadOnlyReason(database);
  const areDatabasesEditable =
    databasesEditable(database?.mode) && areAccountsEditable;
  const offeredExtensions =
    DBServiceParams?.service_name === database?.service_name
      ? extensionCatalogue
      : [];
  const hasExtensions = supportsExtensions(offeredExtensions);
  const extensionTarget = extensionHost(
    database?.version,
    database?.image_flavor
  );
  const installableExtensions = availableExtensions(
    offeredExtensions,
    extensionTarget
  );

  const isInstancePasswordResettable =
    !!database &&
    areAccountsEditable &&
    isServing(database.service_status) &&
    [
      DATABASE_REPLICA_STATUSES.PRIMARY,
      DATABASE_REPLICA_STATUSES.STANDALONE
    ].includes(database.mode);

  const breadcrumbs: Breadcrumb[] = [
    { text: "Organizations", url: ROUTES.ORGANIZATIONS },
    {
      text: organization?.name || "",
      url: generatePath(ROUTES.ORGANIZATION, {
        organizationId: matchParams.organizationId
      })
    },
    {
      text: "Projects",
      url: generatePath(ROUTES.ORGANIZATION, {
        organizationId: matchParams.organizationId
      })
    },
    {
      text: "Databases",
      url: generatePath(ROUTES.DATABASES, {
        organizationId: matchParams.organizationId,
        regionId: matchParams.regionId,
        projectId: matchParams.projectId
      })
    },
    {
      text: database?.name || "",
      url: generatePath(ROUTES.DATABASE, {
        organizationId: matchParams.organizationId,
        regionId: matchParams.regionId,
        projectId: matchParams.projectId,
        serviceName: matchParams.serviceName,
        databaseId: matchParams.databaseId
      })
    }
  ];

  const [copySuccess, setCopySuccess] = useState(false);

  const handleCopyConnectionString = async () => {
    if (database?.connectionString) {
      await navigator.clipboard.writeText(database.connectionString);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000); // Reset after 2 seconds
    }
  };

  const handleChangeTab = useCallback((e, value: number) => {
    setActiveTabIndex(value);
  }, []);

  // Which tabs this engine actually has. Declared once and read by both the tab
  // strip and the guard below, so the strip and the panel under it cannot disagree.
  const isTabVisible = useCallback(
    (tab: number) => {
      if (tab === Number(TABS.REPLICAS)) {
        return supportsReplicas(database?.service_name);
      }
      if (tab === Number(TABS.DATABASES)) {
        return supportsNamedDatabases(database?.service_name);
      }
      if (tab === Number(TABS.CUSTOM_OPTIONS)) {
        return supportsCustomParameters(
          database?.service_name,
          database?.version,
          database?.image_flavor
        );
      }
      return true;
    },
    [database?.service_name, database?.version, database?.image_flavor]
  );

  // The initial tab may not exist for this engine (Redis has no Databases tab)
  useEffect(() => {
    if (!database || isTabVisible(activeTabIndex)) {
      return;
    }
    const firstVisible = Object.keys(TAB_TITLES)
      .map(Number)
      .find((tab) => isTabVisible(tab));
    if (firstVisible !== undefined) {
      setActiveTabIndex(firstVisible);
    }
  }, [database, activeTabIndex, isTabVisible]);

  // const handleActionsMenuButtonClick = useCallback(
  //   (e: MouseEvent<HTMLButtonElement>) => {
  //     actionsMenuButtonRef.current = e.currentTarget;
  //     setIsActionsMenuOpened(!isActionsMenuOpened);
  //   },
  //   [isActionsMenuOpened]
  // );

  const handleActionsMenuClose = useCallback(() => {
    setIsActionsMenuOpened(false);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setDialog({
      ...dialog,
      isOpened: false
    });
  }, [dialog]);

  const handleCreateDatabaseChild = useCallback(
    (data: {
      name: string;
      // Absent on engines with no character sets or collations -- the dialog does
      // not render those fields there (supportsEncodings).
      charset?: SelectOption;
      collation?: SelectOption;
      extensions?: SelectOption[];
    }) => {
      const extensions = selectedExtensionNames(data.extensions);
      const settings = settingsPayload(extensionCatalogue, extensions, data);
      dispatch(
        databasesActions.createDatabaseChildRequest({
          orgId: matchParams.organizationId!,
          projId: matchParams.projectId!,
          serviceName: matchParams.serviceName!,
          id: matchParams.databaseId!,
          data: {
            name: data.name,
            charset: data.charset?.value ?? "",
            collation: data.collation?.value ?? "",
            ...(extensions.length > 0 ? { extensions } : {}),
            ...(Object.keys(settings).length > 0 ? { settings } : {})
          }
        })
      );
      handleCloseDialog();
    },
    [
      dispatch,
      extensionCatalogue,
      handleCloseDialog,
      matchParams.databaseId,
      matchParams.organizationId,
      matchParams.projectId,
      matchParams.serviceName
    ]
  );

  const handleDeleteDatabaseChild = useCallback((id: string) => {
    setSelectedItemId(id);
    setDialog({
      type: DIALOG_TYPES.DELETE_DATABASE_CHILD,
      isOpened: true
    });
  }, []);

  const updateDatabaseChild = useCallback(
    (update: Pick<UpdateDatabaseChildParams, "extensions" | "settings">) => {
      if (selectedItemId) {
        dispatch(
          databasesActions.updateDatabaseChildRequest({
            orgId: matchParams.organizationId!,
            projId: matchParams.projectId!,
            serviceName: matchParams.serviceName!,
            id: matchParams.databaseId!,
            dbName: selectedItemId,
            ...update
          })
        );
      }
      handleCloseDialog();
    },
    [
      dispatch,
      selectedItemId,
      handleCloseDialog,
      matchParams.databaseId,
      matchParams.organizationId,
      matchParams.projectId,
      matchParams.serviceName
    ]
  );

  const handleUpdateDatabaseChildExtensions = useCallback(
    (data: { extensions: SelectOption[] }) =>
      updateDatabaseChild({
        extensions: selectedExtensionNames(data.extensions)
      }),
    [updateDatabaseChild]
  );

  const handleUpdateDatabaseChildSettings = useCallback(
    (data: Record<string, unknown>) => {
      const child = databaseChildren?.find((db) => db.id === selectedItemId);
      updateDatabaseChild({
        settings: settingsPayload(
          extensionCatalogue,
          child?.extensions || [],
          data,
          child?.settings
        )
      });
    },
    [updateDatabaseChild, databaseChildren, selectedItemId, extensionCatalogue]
  );

  const handleCreateDatabaseUser = useCallback(
    (data: {
      name: string;
      permissions: { database: string; permission: string }[];
    }) => {
      dispatch(
        databasesActions.createDatabaseUserRequest({
          orgId: matchParams.organizationId!,
          projId: matchParams.projectId!,
          serviceName: matchParams.serviceName!,
          id: matchParams.databaseId!,
          data
        })
      );
      handleCloseDialog();
    },
    [dispatch, matchParams, handleCloseDialog]
  );
  const handleDeleteDatabaseUser = useCallback((id: string) => {
    setSelectedItemId(id);
    setDialog({
      type: DIALOG_TYPES.DELETE_DATABASE_USER,
      isOpened: true
    });
  }, []);

  const handleSetUserPermissions = useCallback(
    (username: string, permissions: DatabasePermission[]) => {
      dispatch(
        databasesActions.setDatabaseUserPermissionsRequest({
          orgId: matchParams.organizationId!,
          projId: matchParams.projectId!,
          serviceName: matchParams.serviceName!,
          id: matchParams.databaseId!,
          username,
          permissions
        })
      );
      handleCloseDialog();
    },
    [
      dispatch,
      handleCloseDialog,
      matchParams.databaseId,
      matchParams.organizationId,
      matchParams.projectId,
      matchParams.serviceName
    ]
  );

  const handleConfirmDatabasePasswordDialog = useCallback(
    (username: string) => {
      dispatch(
        databasesActions.resetDatabaseUserPasswordRequest({
          orgId: matchParams.organizationId!,
          projId: matchParams.projectId!,
          serviceName: matchParams.serviceName!,
          id: matchParams.databaseId!,
          username
        })
      );
      handleCloseDialog();
    },
    [
      dispatch,
      handleCloseDialog,
      matchParams.databaseId,
      matchParams.organizationId,
      matchParams.projectId,
      matchParams.serviceName
    ]
  );

  const handleChangeDatabaseStatusButtonClick = useCallback(
    (type: CHANGE_STATUS_TYPES) => () => {
      dispatch(
        databasesActions.changeDatabaseStatusRequest({
          orgId: matchParams.organizationId!,
          projId: matchParams.projectId!,
          id: matchParams.databaseId!,
          serviceName: matchParams.serviceName!,
          type: type
        })
      );
    },
    [
      dispatch,
      matchParams.databaseId,
      matchParams.organizationId,
      matchParams.projectId,
      matchParams.serviceName
    ]
  );

  const handleS3UnlinkDatabaseButtonClick = useCallback(() => {
    dispatch(
      databasesActions.s3UnlinkDatabaseRequest({
        orgId: matchParams.organizationId!,
        projId: matchParams.projectId!,
        id: matchParams.databaseId!,
        serviceName: matchParams.serviceName!
      })
    );
  }, [
    dispatch,
    matchParams.databaseId,
    matchParams.organizationId,
    matchParams.projectId,
    matchParams.serviceName
  ]);

  const handleMgrLinkDatabaseButtonClick = useCallback(() => {
    dispatch(
      databasesActions.getManagementUrlRequest({
        orgId: matchParams.organizationId!,
        projId: matchParams.projectId!,
        id: matchParams.databaseId!,
        serviceName: matchParams.serviceName!
      })
    );
  }, [
    dispatch,
    matchParams.databaseId,
    matchParams.organizationId,
    matchParams.projectId,
    matchParams.serviceName
  ]);

  const handleEditDatabaseButtonClick = useCallback(() => {
    setDialog({
      type: DIALOG_TYPES.EDIT,
      isOpened: true
    });
  }, []);

  const handleDeleteDatabaseButtonClick = useCallback(() => {
    setDialog({
      type: DIALOG_TYPES.DELETE,
      isOpened: true
    });
  }, []);

  const handleRestoreDatabaseButtonClick = useCallback((id: string) => {
    setSelectedItemId(id);
    setDialog({
      type: DIALOG_TYPES.RESTORE_BACKUP,
      isOpened: true
    });
  }, []);

  const selectedCustomOption = databaseCustomOptions?.find(
    (option) => option.name === selectedItemId
  );

  const handleEditDatabaseCustomOptionsButtonClick = useCallback(
    (id: string) => {
      setSelectedItemId(id);
      setDialog({
        type: DIALOG_TYPES.EDIT_CUSTOM_OPTION,
        isOpened: true
      });
    },
    []
  );

  const handleRestoreSetButtonClick = useCallback((id: string) => {
    setSelectedItemId(id);
    setDialog({
      type: DIALOG_TYPES.RESTORE_SET,
      isOpened: true
    });
  }, []);

  const handleRollbackButtonClick = useCallback(() => {
    setDialog({
      type: DIALOG_TYPES.ROLLBACK_RESTORE,
      isOpened: true
    });
  }, []);

  const handleCreateBackupButtonClick = useCallback(() => {
    setDialog({
      type: DIALOG_TYPES.CREATE_BACKUP,
      isOpened: true
    });
  }, []);

  const handleS3LinkButtonClick = useCallback(
    () => setStorageLinkOpened(true),
    []
  );

  const handleCreateReplicaButtonClick = useCallback(() => {
    setDialog({
      type: DIALOG_TYPES.CREATE_REPLICA,
      isOpened: true
    });
  }, []);

  const handleCronButtonClick = useCallback(() => {
    dispatch(databasesActions.clearBackupScheduleResult());
    setScheduleOpened(true);
  }, [dispatch]);

  useEffect(() => {
    if (scheduleResult.saved) setScheduleOpened(false);
  }, [scheduleResult.saved]);

  const handleCronDeleteButtonClick = useCallback(() => {
    setDialog({
      type: DIALOG_TYPES.DELETE_CRON,
      isOpened: true
    });
  }, []);

  const handleDeleteReplicaMenuItemClick = useCallback((id: string) => {
    setSelectedItemId(id);
    setDialog({
      type: DIALOG_TYPES.DELETE_REPLICA,
      isOpened: true
    });
  }, []);

  const backupsTableActions: TableRowActionsMenuItem<TableDatabaseBackupFiles>[] =
    [
      {
        label: "Restore",
        isDisabled: (file) =>
          areBackupOrRestoreDisabled || Boolean(file.unattributed),
        handler: handleRestoreDatabaseButtonClick
      }
    ];

  const backupSetsTableActions: TableRowActionsMenuItem<TableBackupSet>[] = [
    {
      label: "Restore...",
      isDisabled: () => areBackupOrRestoreDisabled,
      handler: handleRestoreSetButtonClick
    }
  ];

  const customOptionsTableActions: TableRowActionsMenuItem<TableDatabaseCustomOptions>[] =
    [
      {
        label: "Edit",
        handler: handleEditDatabaseCustomOptionsButtonClick,
        isDisabled: (option) =>
          option.type === "readonly" ||
          option.editable === "false" ||
          database?.mode === DATABASE_REPLICA_STATUSES.REPLICA
      },
      {
        label: "Unset override",
        isDisabled: (option) =>
          option.has_custom_value === "false" ||
          (option.has_custom_value === undefined && !option.custom_value) ||
          database?.mode === DATABASE_REPLICA_STATUSES.REPLICA,
        handler: (name) =>
          dispatch(
            databasesActions.updateDatabaseCustomOptionsRequest({
              orgId: matchParams.organizationId!,
              projId: matchParams.projectId!,
              id: matchParams.databaseId!,
              serviceName: matchParams.serviceName!,
              data: [],
              unset: name
            })
          )
      }
    ];

  const replicaTableActions: TableRowActionsMenuItem<TableReplicaDatabase>[] = [
    {
      label: "Delete",
      handler: handleDeleteReplicaMenuItemClick
    }
  ];

  const databaseChildrenTableActions: TableRowActionsMenuItem<TableDatabaseChild>[] =
    [
      // A replica's databases follow its primary; the API refuses the delete.
      ...(areDatabasesEditable
        ? [
            {
              label: "Delete",
              handler: handleDeleteDatabaseChild
            }
          ]
        : []),
      ...(hasExtensions && areDatabasesEditable
        ? [
            {
              label: "Extensions",
              handler: (id: string) => {
                setSelectedItemId(id);
                setDialog({
                  type: DIALOG_TYPES.EDIT_DATABASE_CHILD_EXTENSIONS,
                  isOpened: true
                });
              },
              // Nothing to offer for this engine and version, and nothing to remove.
              isDisabled: (row: TableDatabaseChild) =>
                installableExtensions.length === 0 &&
                (row.extensions || []).length === 0
            },
            {
              label: "Settings",
              handler: (id: string) => {
                setSelectedItemId(id);
                setDialog({
                  type: DIALOG_TYPES.EDIT_DATABASE_CHILD_SETTINGS,
                  isOpened: true
                });
              },
              isDisabled: (row: TableDatabaseChild) =>
                extensionSettings(extensionCatalogue, row.extensions || [])
                  .length === 0
            }
          ]
        : [])
    ];

  const databaseUsersTableActions: TableRowActionsMenuItem<TableDatabaseUser>[] =
    areAccountsEditable
      ? [
          {
            label: "Delete",
            handler: handleDeleteDatabaseUser
          },
          {
            label: "Set Permissions",
            handler: (id: string) => {
              setSelectedItemId(id);
              setDialog({ type: DIALOG_TYPES.SET_PERMISSIONS, isOpened: true });
            }
          },
          {
            label: "Reset Password",
            handler: (id: string) => {
              setSelectedItemId(id);
              setDialog({ type: DIALOG_TYPES.RESET_PASSWORD, isOpened: true });
            }
          }
        ]
      : [];

  useMount(() => {
    dispatch(
      createPolling({
        id: `${POLL_ID_PREFIX}/${POLL_IDS.database}`,
        actionCreator: databasesActions.getDatabaseRequest,
        params: {
          orgId: matchParams.organizationId!,
          projId: matchParams.projectId!,
          id: matchParams.databaseId!,
          serviceName: matchParams.serviceName!
        }
      })
    );
    dispatch(
      createPolling({
        id: `${POLL_ID_PREFIX}/${POLL_IDS.databaseMetrics}`,
        actionCreator: databasesActions.getDatabaseMetricsRequest,
        params: {
          orgId: matchParams.organizationId!,
          projId: matchParams.projectId!,
          id: matchParams.databaseId!,
          serviceName: matchParams.serviceName!,
          duration: DATABASE_METRIC_DEFAULT_DURATION,
          range: DATABASE_METRIC_DEFAULT_RANGE
        }
      })
    );
    dispatch(
      createPolling({
        id: `${POLL_ID_PREFIX}/${POLL_IDS.databaseBackupStatus}`,
        actionCreator: databasesActions.getDatabaseBackupStatusRequest,
        params: {
          orgId: matchParams.organizationId!,
          projId: matchParams.projectId!,
          id: matchParams.databaseId!,
          serviceName: matchParams.serviceName!
        }
      })
    );
    dispatch(
      enterprisesActions.getOrganizationRequest({
        id: matchParams.organizationId!
      })
    );
    dispatch(
      projectsActions.getProjectRequest({
        regionId: matchParams.regionId!,
        id: matchParams.projectId!
      })
    );
    dispatch(
      projectsActions.getOrganizationProjectsRequest({
        organizationId: matchParams.organizationId!
      })
    );
    dispatch(
      databasesActions.getDBServiceParamsRequest({
        serviceName: matchParams.serviceName!
      })
    );
  });

  useUnmount(() => {
    Object.values(POLL_IDS).forEach((id) => {
      dispatch(
        pollingActions.stopPolling({
          id: `${POLL_ID_PREFIX}/${id}`
        })
      );
    });
    dispatch(enterprisesActions.clear());
    dispatch(projectsActions.clear());
    dispatch(databasesActions.clear());
  });

  useEffect(() => {
    if (previousDatabaseId !== matchParams.databaseId) {
      setStorageLinkOpened(false);
      setScheduleOpened(false);
      Object.values(POLL_IDS).forEach((id) => {
        dispatch(
          pollingActions.stopPolling({
            id: `${POLL_ID_PREFIX}/${id}`
          })
        );
      });
      dispatch(enterprisesActions.clear());
      dispatch(projectsActions.clear());
      dispatch(databasesActions.clear());
      dispatch(
        createPolling({
          id: `${POLL_ID_PREFIX}/${POLL_IDS.database}`,
          actionCreator: databasesActions.getDatabaseRequest,
          params: {
            orgId: matchParams.organizationId!,
            projId: matchParams.projectId!,
            id: matchParams.databaseId!,
            serviceName: matchParams.serviceName!
          }
        })
      );
      dispatch(
        createPolling({
          id: `${POLL_ID_PREFIX}/${POLL_IDS.databaseMetrics}`,
          actionCreator: databasesActions.getDatabaseMetricsRequest,
          params: {
            orgId: matchParams.organizationId!,
            projId: matchParams.projectId!,
            id: matchParams.databaseId!,
            serviceName: matchParams.serviceName!,
            duration: DATABASE_METRIC_DEFAULT_DURATION,
            range: DATABASE_METRIC_DEFAULT_RANGE
          }
        })
      );
      dispatch(
        createPolling({
          id: `${POLL_ID_PREFIX}/${POLL_IDS.databaseBackupStatus}`,
          actionCreator: databasesActions.getDatabaseBackupStatusRequest,
          params: {
            orgId: matchParams.organizationId!,
            projId: matchParams.projectId!,
            id: matchParams.databaseId!,
            serviceName: matchParams.serviceName!
          }
        })
      );
      dispatch(
        enterprisesActions.getOrganizationRequest({
          id: matchParams.organizationId!
        })
      );
      dispatch(
        projectsActions.getProjectRequest({
          regionId: matchParams.regionId!,
          id: matchParams.projectId!
        })
      );
      dispatch(
        projectsActions.getOrganizationProjectsRequest({
          organizationId: matchParams.organizationId!
        })
      );
      dispatch(
        databasesActions.getDBServiceParamsRequest({
          serviceName: matchParams.serviceName!
        })
      );
    }
  }, [
    dispatch,
    matchParams.projectId,
    matchParams.regionId,
    matchParams.organizationId,
    matchParams.databaseId,
    matchParams.serviceName,
    previousDatabaseId
  ]);

  useEffect(() => {
    if (previousIsOperationInProgress && !isOperationInProgress) {
      if (!database) {
        history(getParentPath(location.pathname, 2));
      } else {
        dispatch(
          databasesActions.getDatabaseRequest({
            orgId: matchParams.organizationId!,
            projId: matchParams.projectId!,
            serviceName: matchParams.serviceName!,
            id: matchParams.databaseId!
          })
        );
        dispatch(
          databasesActions.getDatabaseBackupStatusRequest({
            orgId: matchParams.organizationId!,
            projId: matchParams.projectId!,
            serviceName: matchParams.serviceName!,
            id: matchParams.databaseId!
          })
        );
      }
    }
  }, [
    database,
    history,
    previousIsOperationInProgress,
    isOperationInProgress,
    dispatch,
    matchParams.projectId,
    matchParams.organizationId,
    matchParams.databaseId,
    matchParams.serviceName
  ]);

  useEffect(() => {
    if (
      previousActiveTabIndex !== activeTabIndex ||
      previousDatabaseId !== matchParams.databaseId
    ) {
      dispatch(databasesActions.clearBackupFiles());
      dispatch(databasesActions.clearReplicaDatabases());
      dispatch(databasesActions.clearDatabaseUsers());
      dispatch(databasesActions.clearDatabaseChildren());
      switch (activeTabIndex) {
        case TABS.DATABASES:
          history({
            search: generateSearchString({
              tab: TAB_TITLES[TABS.DATABASES]
            })
          });
          pollingActions.stopPolling({
            id: `${POLL_ID_PREFIX}/${POLL_IDS.databaseChildren}`
          });
          pollingActions.stopPolling({
            id: `${POLL_ID_PREFIX}/${POLL_IDS.databaseEncodings}`
          });
          dispatch(
            createPolling({
              id: `${POLL_ID_PREFIX}/${POLL_IDS.databaseChildren}`,
              actionCreator: databasesActions.getDatabaseChildrenRequest,
              params: {
                orgId: matchParams.organizationId!,
                projId: matchParams.projectId!,
                serviceName: matchParams.serviceName!,
                id: matchParams.databaseId!
              }
            })
          );
          dispatch(
            createPolling({
              id: `${POLL_ID_PREFIX}/${POLL_IDS.databaseEncodings}`,
              actionCreator: databasesActions.getDatabaseEncodingsRequest,
              params: {
                orgId: matchParams.organizationId!,
                projId: matchParams.projectId!,
                serviceName: matchParams.serviceName!,
                id: matchParams.databaseId!
              }
            })
          );
          break;
        case TABS.USERS:
          history({
            search: generateSearchString({
              tab: TAB_TITLES[TABS.USERS]
            })
          });
          pollingActions.stopPolling({
            id: `${POLL_ID_PREFIX}/${POLL_IDS.databaseUsers}`
          });
          pollingActions.stopPolling({
            id: `${POLL_ID_PREFIX}/${POLL_IDS.databaseChildren}`
          });
          dispatch(
            createPolling({
              id: `${POLL_ID_PREFIX}/${POLL_IDS.databaseChildren}`,
              actionCreator: databasesActions.getDatabaseChildrenRequest,
              params: {
                orgId: matchParams.organizationId!,
                projId: matchParams.projectId!,
                serviceName: matchParams.serviceName!,
                id: matchParams.databaseId!
              }
            })
          );
          dispatch(
            createPolling({
              id: `${POLL_ID_PREFIX}/${POLL_IDS.databaseUsers}`,
              actionCreator: databasesActions.getDatabaseUsersRequest,
              params: {
                orgId: matchParams.organizationId!,
                projId: matchParams.projectId!,
                serviceName: matchParams.serviceName!,
                id: matchParams.databaseId!
              }
            })
          );
          break;
        case TABS.REPLICAS:
          history({
            search: generateSearchString({
              tab: TAB_TITLES[TABS.REPLICAS]
            })
          });
          pollingActions.stopPolling({
            id: `${POLL_ID_PREFIX}/${POLL_IDS.replicaDatabases}`
          });
          dispatch(
            createPolling({
              id: `${POLL_ID_PREFIX}/${POLL_IDS.replicaDatabases}`,
              actionCreator: databasesActions.getReplicaDatabasesRequest,
              params: {
                orgId: matchParams.organizationId!,
                projId: matchParams.projectId!,
                id: matchParams.databaseId!,
                serviceName: matchParams.serviceName!
              }
            })
          );
          break;
        case TABS.BACKUPS:
          history({
            search: generateSearchString({
              tab: TAB_TITLES[TABS.BACKUPS]
            })
          });
          pollingActions.stopPolling({
            id: `${POLL_ID_PREFIX}/${POLL_IDS.databaseChildren}`
          });
          dispatch(
            createPolling({
              id: `${POLL_ID_PREFIX}/${POLL_IDS.databaseChildren}`,
              actionCreator: databasesActions.getDatabaseChildrenRequest,
              params: {
                orgId: matchParams.organizationId!,
                projId: matchParams.projectId!,
                serviceName: matchParams.serviceName!,
                id: matchParams.databaseId!
              }
            })
          );
          break;
        case TABS.NETWORK:
          history({
            search: generateSearchString({ tab: TAB_TITLES[TABS.NETWORK] })
          });
          break;
        case TABS.CUSTOM_OPTIONS:
          history({
            search: generateSearchString({
              tab: TAB_TITLES[TABS.CUSTOM_OPTIONS]
            })
          });
          break;
      }
    }
  }, [
    previousDatabaseId,
    previousActiveTabIndex,
    activeTabIndex,
    backupStatus,
    backupFiles,
    history,
    dispatch,
    matchParams.projectId,
    matchParams.organizationId,
    matchParams.regionId,
    matchParams.databaseId,
    matchParams.serviceName
  ]);

  useEffect(() => {
    if (backupStatus?.link_exist && activeTabIndex === TABS.BACKUPS) {
      dispatch(
        createPolling({
          id: `${POLL_ID_PREFIX}/${POLL_IDS.databaseBackupFiles}`,
          actionCreator: databasesActions.getDatabaseBackupFilesRequest,
          params: {
            orgId: matchParams.organizationId!,
            projId: matchParams.projectId!,
            id: matchParams.databaseId!,
            serviceName: matchParams.serviceName!
          }
        })
      );
    }
    if (backupStatus?.link_exist === false)
      dispatch(databasesActions.clearBackupFiles());
    return () => {
      dispatch(
        pollingActions.stopPolling({
          id: `${POLL_ID_PREFIX}/${POLL_IDS.databaseBackupFiles}`
        })
      );
    };
  }, [
    activeTabIndex,
    backupStatus?.link_exist,
    backupStatus?.s3endpoint,
    backupStatus?.s3bucket,
    dispatch,
    matchParams.projectId,
    matchParams.organizationId,
    matchParams.databaseId,
    matchParams.serviceName
  ]);

  useEffect(() => {
    if (activeTabIndex === TABS.CUSTOM_OPTIONS) {
      dispatch(
        pollingActions.stopPolling({
          id: `${POLL_ID_PREFIX}/${POLL_IDS.databaseCustomOptions}`
        })
      );
      dispatch(
        createPolling({
          id: `${POLL_ID_PREFIX}/${POLL_IDS.databaseCustomOptions}`,
          actionCreator: databasesActions.getDatabaseCustomOptionsRequest,
          params: {
            orgId: matchParams.organizationId!,
            projId: matchParams.projectId!,
            id: matchParams.databaseId!,
            serviceName: matchParams.serviceName!
          }
        })
      );
    }
  }, [
    activeTabIndex,
    dispatch,
    matchParams.projectId,
    matchParams.organizationId,
    matchParams.databaseId,
    matchParams.serviceName
  ]);

  useEffect(() => {
    setIsDatabasePasswordDialogOpened(true);
  }, [databaseResetedPassword]);

  // The operator may create the user's secret after the API answers.
  useEffect(() => {
    if (databaseUser && !databaseUser.password) {
      dispatch(
        notificationsActions.showNotification({
          type: NOTIFICATION_TYPES.INFO,
          title: `User "${databaseUser.user}" created`,
          text: "Password not available yet — use Reset password."
        })
      );
      dispatch(databasesActions.clearDatabaseUser());
    }
  }, [dispatch, databaseUser]);

  const sendEditDatabase = useCallback(
    (data: EditDatabaseFormData) => {
      const hadHA = Boolean(database?.high_availability?.enabled);
      const pooler = poolerUpdate(
        database?.pooler,
        data.pooler_enabled,
        data.pooler_mode?.value as PoolerMode | undefined
      );
      dispatch(
        databasesActions.updateDatabaseRequest({
          orgId: matchParams.organizationId!,
          projId: matchParams.projectId!,
          serviceName: matchParams.serviceName!,
          id: matchParams.databaseId!,
          data: {
            // Compared with the SPEC version: re-sending the running one mid-upgrade
            // reverts spec.version, which cancels the upgrade.
            ...(data.version.value !== database?.version
              ? { version: data.version.value }
              : {}),
            cpu: Number(data.cpu.value),
            memory: Number(data.memory.value),
            // Sent only when changed: the API refuses a smaller value
            ...(Number(data.disk) !== database?.disk
              ? { disk: Number(data.disk) }
              : {}),
            ...(Number(data.port) > 0 && Number(data.port) !== database?.port
              ? { port: Number(data.port) }
              : {}),
            ...(data.ha_enabled !== hadHA
              ? { high_availability: { enabled: data.ha_enabled } }
              : {}),
            // Only a change is sent: an unrelated edit must not touch the pooler.
            ...(pooler ? { pooler } : {})
          }
        })
      );
      handleCloseDialog();
    },
    [
      dispatch,
      matchParams.organizationId,
      matchParams.projectId,
      matchParams.serviceName,
      matchParams.databaseId,
      database?.high_availability?.enabled,
      database?.pooler,
      database?.disk,
      database?.version,
      database?.port,
      handleCloseDialog
    ]
  );

  const handleEditDatabaseCustomOptions = useCallback(
    (raw: string | SelectOption) => {
      const value = typeof raw === "string" ? raw : raw?.value;
      const setting = selectedCustomOption
        ? customParameterSetting(selectedCustomOption)
        : undefined;
      // The dialog prefills the default when nothing is set; saving it untouched adds no override.
      const prefilled =
        selectedCustomOption?.has_custom_value === "false"
          ? selectedCustomOption.default_value
          : selectedCustomOption?.custom_value;
      const unchanged =
        setting &&
        normalizeSettingValue(setting, value ?? "") ===
          normalizeSettingValue(setting, prefilled ?? "");
      if (
        value === undefined ||
        (value === "" && !setting?.allowEmpty) ||
        unchanged
      ) {
        handleCloseDialog();
        return;
      }
      dispatch(
        databasesActions.updateDatabaseCustomOptionsRequest({
          orgId: matchParams.organizationId!,
          projId: matchParams.projectId!,
          id: matchParams.databaseId!,
          serviceName: matchParams.serviceName!,
          data: [
            {
              name: selectedItemId || "",
              value: String(value)
            }
          ]
        })
      );
      handleCloseDialog();
    },
    [
      dispatch,
      matchParams.organizationId,
      matchParams.projectId,
      matchParams.serviceName,
      matchParams.databaseId,
      selectedItemId,
      selectedCustomOption,
      handleCloseDialog
    ]
  );
  const handleS3LinkDatabase = useCallback(
    (data: {
      endpoint: string;
      bucket: string;
      key: string;
      secret: string;
    }) => {
      dispatch(
        databasesActions.s3LinkDatabaseRequest({
          orgId: matchParams.organizationId!,
          projId: matchParams.projectId!,
          serviceName: matchParams.serviceName!,
          id: matchParams.databaseId!,
          data
        })
      );
    },
    [
      dispatch,
      matchParams.organizationId,
      matchParams.projectId,
      matchParams.serviceName,
      matchParams.databaseId
    ]
  );

  const handleCreateReplicaDatabase = useCallback(
    (data: { replProjId: SelectOption }) => {
      dispatch(
        databasesActions.createReplicaDatabaseRequest({
          orgId: matchParams.organizationId!,
          projId: matchParams.projectId!,
          serviceName: matchParams.serviceName!,
          id: matchParams.databaseId!,
          data: {
            project_id: data.replProjId.value
          }
        })
      );
      handleCloseDialog();
    },
    [
      dispatch,
      matchParams.organizationId,
      matchParams.projectId,
      matchParams.serviceName,
      matchParams.databaseId,
      handleCloseDialog
    ]
  );

  const handleCronBackupApply = useCallback(
    (data: { cron: string; retention: string; timezone: SelectOption }) => {
      dispatch(
        databasesActions.cronBackupRequest({
          orgId: matchParams.organizationId!,
          projId: matchParams.projectId!,
          serviceName: matchParams.serviceName!,
          id: matchParams.databaseId!,
          data: {
            cron: data.cron,
            retention: data.retention,
            timezone: data.timezone.value
          }
        })
      );
    },
    [
      dispatch,
      matchParams.organizationId,
      matchParams.projectId,
      matchParams.serviceName,
      matchParams.databaseId
    ]
  );

  const handleCronDeleteBackup = useCallback(() => {
    dispatch(
      databasesActions.cronDeleteRequest({
        orgId: matchParams.organizationId!,
        projId: matchParams.projectId!,
        serviceName: matchParams.serviceName!,
        id: matchParams.databaseId!
      })
    );
    handleCloseDialog();
  }, [
    dispatch,
    matchParams.organizationId,
    matchParams.projectId,
    matchParams.serviceName,
    matchParams.databaseId,
    handleCloseDialog
  ]);

  const handleConfirmDeleteDatabase = useCallback(() => {
    dispatch(
      databasesActions.deleteDatabaseRequest({
        orgId: matchParams.organizationId!,
        projId: matchParams.projectId!,
        serviceName: matchParams.serviceName!,
        id: matchParams.databaseId!
      })
    );
    handleCloseDialog();
  }, [
    dispatch,
    matchParams.organizationId,
    matchParams.projectId,
    matchParams.serviceName,
    matchParams.databaseId,
    handleCloseDialog
  ]);

  const handleConfirmDeleteReplica = useCallback(() => {
    if (selectedItemId && selectedReplicaDatabaseProjectId) {
      dispatch(
        databasesActions.deleteDatabaseRequest({
          orgId: matchParams.organizationId!,
          projId: selectedReplicaDatabaseProjectId,
          serviceName: matchParams.serviceName!,
          id: selectedItemId
        })
      );
      handleCloseDialog();
    }
  }, [
    dispatch,
    matchParams.organizationId,
    matchParams.serviceName,
    selectedReplicaDatabaseProjectId,
    selectedItemId,
    handleCloseDialog
  ]);

  const handleConfirmDeleteDatabaseChild = useCallback(() => {
    if (selectedItemId) {
      dispatch(
        databasesActions.deleteDatabaseChildRequest({
          orgId: matchParams.organizationId!,
          projId: matchParams.projectId!,
          serviceName: matchParams.serviceName!,
          id: matchParams.databaseId!,
          dbName: selectedItemId
        })
      );
    }
    handleCloseDialog();
  }, [
    selectedItemId,
    handleCloseDialog,
    dispatch,
    matchParams.organizationId,
    matchParams.projectId,
    matchParams.serviceName,
    matchParams.databaseId
  ]);

  const handleConfirmDeleteDatabaseUser = useCallback(() => {
    if (selectedItemId) {
      dispatch(
        databasesActions.deleteDatabaseUserRequest({
          orgId: matchParams.organizationId!,
          projId: matchParams.projectId!,
          serviceName: matchParams.serviceName!,
          id: matchParams.databaseId!,
          username: selectedItemId
        })
      );
    }
    handleCloseDialog();
  }, [
    selectedItemId,
    handleCloseDialog,
    dispatch,
    matchParams.organizationId,
    matchParams.projectId,
    matchParams.serviceName,
    matchParams.databaseId
  ]);

  const handleRestoreDatabase = useCallback(
    // The dialog has no database field for engines that restore the whole
    // instance, so the payload is optional here.
    (data?: { database?: SelectOption }) => {
      if (selectedItemId && backupFiles) {
        const file = backupFiles?.find((file) => file.etag === selectedItemId);
        if (file && !file.unattributed) {
          dispatch(
            databasesActions.restoreDatabaseRequest({
              orgId: matchParams.organizationId!,
              projId: matchParams.projectId!,
              serviceName: matchParams.serviceName!,
              id: matchParams.databaseId!,
              data: {
                database: data?.database?.value ?? "",
                file: file.file_name
              }
            })
          );
        }

        handleCloseDialog();
      }
    },
    [
      dispatch,
      selectedItemId,
      backupFiles,
      matchParams.organizationId,
      matchParams.projectId,
      matchParams.serviceName,
      matchParams.databaseId,
      handleCloseDialog
    ]
  );

  // Restore several objects, each into its own database; the API refuses the set if any entry is bad
  const handleRestoreSet = useCallback(
    (data?: { databases?: SelectOption[]; safetyBackup?: boolean }) => {
      const set = rawBackupSets?.find((x) => x.id === selectedItemId);
      if (!set) {
        return;
      }

      // An empty selection restores every database in the set
      const chosen = data?.databases?.map((option) => option.value);
      const files = set.files.filter(
        (file) => !chosen?.length || chosen.includes(file.database || "")
      );
      if (!files.length) {
        return;
      }

      dispatch(
        databasesActions.restoreDatabaseRequest({
          orgId: matchParams.organizationId!,
          projId: matchParams.projectId!,
          serviceName: matchParams.serviceName!,
          id: matchParams.databaseId!,
          data: {
            // Ignored by the API when items is present; sent so the payload is valid
            // for a client reading the older contract.
            database: "",
            file: "",
            items: files.map((file) => ({
              file: file.file_name,
              database: file.database
            })),
            // Sent only to turn the copy OFF. Absent means the API's default, which is
            // to take one -- and that default is what makes a restore reversible.
            ...(data?.safetyBackup === false ? { safety_backup: false } : {})
          }
        })
      );
      handleCloseDialog();
    },
    [
      dispatch,
      rawBackupSets,
      selectedItemId,
      matchParams.organizationId,
      matchParams.projectId,
      matchParams.serviceName,
      matchParams.databaseId,
      handleCloseDialog
    ]
  );

  const handleRollbackRestore = useCallback(() => {
    dispatch(
      databasesActions.rollbackRestoreRequest({
        orgId: matchParams.organizationId!,
        projId: matchParams.projectId!,
        serviceName: matchParams.serviceName!,
        id: matchParams.databaseId!
      })
    );
    handleCloseDialog();
  }, [
    dispatch,
    matchParams.organizationId,
    matchParams.projectId,
    matchParams.serviceName,
    matchParams.databaseId,
    handleCloseDialog
  ]);

  const handleBackupDatabase = useCallback(
    (data?: { databases?: SelectOption[] }) => {
      // Empty means every database, which is the API's default and the only safe
      // reading of "the tenant expressed no preference".
      const chosen = data?.databases?.map((option) => option.value) || [];
      dispatch(
        databasesActions.backupDatabaseRequest({
          orgId: matchParams.organizationId!,
          projId: matchParams.projectId!,
          serviceName: matchParams.serviceName!,
          id: matchParams.databaseId!,
          data: {
            timezone: CLIENT_TIME_ZONE,
            ...(chosen.length ? { databases: chosen } : {})
          }
        })
      );
      handleCloseDialog();
    },
    [
      dispatch,
      matchParams.organizationId,
      matchParams.projectId,
      matchParams.serviceName,
      matchParams.databaseId,
      handleCloseDialog
    ]
  );

  const handleResetDefaultUserPassword = useCallback(() => {
    dispatch(
      databasesActions.resetDatabasePasswordRequest({
        orgId: matchParams.organizationId!,
        projId: matchParams.projectId!,
        serviceName: matchParams.serviceName!,
        id: matchParams.databaseId!
      })
    );
    setIsResetDefaultPasswordDialogOpened(false);
  }, [
    dispatch,
    matchParams.organizationId,
    matchParams.projectId,
    matchParams.serviceName,
    matchParams.databaseId
  ]);

  const handleCloseDatabasePasswordDialog = useCallback(() => {
    setIsDatabasePasswordDialogOpened(false);
    dispatch(databasesActions.clearDatabaseResetPassword());
    dispatch(databasesActions.clearDatabaseUser());
  }, [dispatch]);

  // Redis grants are server-wide: one level for the instance, not one per database
  const grantsPerDatabase = supportsPerDatabaseGrants(database?.service_name);
  const selectedChild = databaseChildren?.find(
    (db) => db.id === selectedItemId
  );
  const selectedChildExtensions = selectedChild?.extensions || [];
  const selectedBackupFile = backupFiles?.find(
    (file) => file.etag === selectedItemId
  );
  const removedBackupSource =
    selectedBackupFile?.database &&
    !selectedBackupFile.taken &&
    !databaseChildren?.some((db) => db.name === selectedBackupFile.database)
      ? selectedBackupFile.database
      : undefined;
  const extensionsOnInstance = installedExtensions(databaseChildren || []);
  const editExtensionOptions = extensionOptions(offeredExtensions, {
    current: selectedChildExtensions,
    host: extensionTarget
  });

  const versionOptions = versionSelectOptions(database);
  // The children load only on some tabs; the retained list covers every database.
  const upgradeExtensionsWarning = (fieldValues: Record<string, unknown>) =>
    isSelectOption(fieldValues.version)
      ? upgradeWarning(
          strandedByUpgrade(
            offeredExtensions,
            extensionsInUse(
              database?.retained_extensions,
              databaseChildren || []
            ),
            extensionTarget,
            extensionHost(fieldValues.version.value, database?.image_flavor)
          ),
          fieldValues.version.value
        )
      : "";
  const migration = migrationAlert(database?.migration);

  // An instance may already be larger than the catalogue maximum.
  const diskMax = DBServiceParams?.disk_max
    ? Math.max(Number(DBServiceParams.disk_max) || 0, database?.disk || 0)
    : undefined;

  const dialogProps: {
    [key in DIALOG_TYPES]: Omit<FormDialogProps, "isOpened" | "onCancel">;
  } = {
    [DIALOG_TYPES.EDIT]: {
      onConfirm: sendEditDatabase,
      title: "Edit database",
      confirmButtonLabel: "Save",
      fields: [
        {
          name: "version",
          type: FIELD_TYPES.SELECT,
          label: "Version",
          // Preselects the SPEC version so an unrelated save is not a version
          // change; picking the "(current)" entry is how a parked upgrade is cancelled.
          defaultValue:
            database &&
            (versionOptions.find((o) => o.value === database.version) ??
              getSelectOption(database, "version", "version")),
          options: versionOptions,
          rules: selectOptionSchema
        },
        {
          name: "versionExtensionsWarning",
          type: FIELD_TYPES.NOTES,
          label: upgradeExtensionsWarning,
          isHidden: (fieldValues) => !upgradeExtensionsWarning(fieldValues)
        },
        {
          name: "port",
          type: FIELD_TYPES.NUMBER,
          label: "Port",
          defaultValue: database?.port ?? "",
          min: MIN_PORT,
          max: MAX_PORT,
          rules: (isNumber(database?.port)
            ? number().required()
            : number().optional()
          )
            .transform((value: number, originalValue: unknown) =>
              isString(originalValue) && originalValue.trim() === ""
                ? undefined
                : value
            )
            .integer()
            .min(
              MIN_PORT,
              `Port must be greater than or equals to ${MIN_PORT}.`
            )
            .max(MAX_PORT, `Port must be less than ${MAX_PORT}.`)
        },
        {
          name: "cpu",
          type: FIELD_TYPES.SELECT,
          label: "CPU",
          defaultValue: database && {
            label: `${database.cpuString} vCPU`,
            value: String(database.cpu)
          },
          options: DBServiceParams?.cpu.map((x) => ({
            label: `${Number(x) / 1000} vCPU`,
            value: x
          })),
          rules: selectOptionSchema
        },
        {
          name: "memory",
          type: FIELD_TYPES.SELECT,
          label: "RAM",
          defaultValue:
            database && getSelectOption(database, "memoryString", "memory"),
          options: DBServiceParams?.memory.map((x) => ({
            label: `${Number(x) / 1024} GiB`,
            value: x
          })),
          rules: selectOptionSchema
        },
        {
          // Grow only: volume expansion is one-way, so the floor is the current size
          name: "disk",
          type: FIELD_TYPES.NUMBER,
          label: "Disk",
          suffix: "MiB",
          defaultValue: database?.disk,
          min: database?.disk,
          max: diskMax,
          rules: (() => {
            const rules = number()
              .integer()
              .min(
                database?.disk || 0,
                `Disk can only be increased. The current size is ${
                  database?.disk || 0
                } MiB.`
              );
            return isUndefined(diskMax)
              ? rules
              : rules.max(diskMax, `Disk must not exceed ${diskMax} MiB.`);
          })()
        },
        {
          name: "ha_enabled",
          type: FIELD_TYPES.CHECKBOX,
          label: "Enable High Availability",
          defaultValue: Boolean(database?.high_availability?.enabled)
        },
        ...(poolerEditable(database)
          ? [
              {
                name: "pooler_enabled",
                type: FIELD_TYPES.CHECKBOX,
                label:
                  "Enable connection pooler (PgBouncer, separate endpoint)",
                defaultValue: Boolean(database?.pooler?.enabled)
              },
              {
                name: "pooler_mode",
                type: FIELD_TYPES.SELECT,
                label: "Pooling mode",
                options: POOLER_MODE_OPTIONS,
                defaultValue:
                  POOLER_MODE_OPTIONS.find(
                    (o) => o.value === (database?.pooler?.mode ?? "transaction")
                  ) ?? POOLER_MODE_OPTIONS[0],
                isHidden: (fieldValues: Record<string, unknown>) =>
                  !fieldValues.pooler_enabled
              },
              {
                name: "poolerModeInfo",
                type: FIELD_TYPES.NOTES,
                label: (fieldValues: Record<string, unknown>) =>
                  isSelectOption(fieldValues.pooler_mode)
                    ? POOLER_MODE_DESCRIPTIONS[
                        fieldValues.pooler_mode.value as PoolerMode
                      ]
                    : "",
                isHidden: (fieldValues: Record<string, unknown>) =>
                  !fieldValues.pooler_enabled
              },
              {
                name: "poolerDisconnectInfo",
                type: FIELD_TYPES.NOTES,
                label: POOLER_DISCONNECT_WARNING,
                // Shown before Save, so the edit is never lost to a second dialog.
                isHidden: (fieldValues: Record<string, unknown>) =>
                  !poolerDisconnectsClients(
                    database?.pooler,
                    poolerUpdate(
                      database?.pooler,
                      Boolean(fieldValues.pooler_enabled),
                      (fieldValues.pooler_mode as SelectOption | undefined)
                        ?.value as PoolerMode | undefined
                    )
                  )
              }
            ]
          : [])
      ]
    },
    [DIALOG_TYPES.DELETE]: {
      onConfirm: handleConfirmDeleteDatabase,
      title: `Are you sure you want to delete "${
        database?.name ?? "selected"
      }" database?`,
      fields: [
        {
          name: "confirmationName",
          type: FIELD_TYPES.TEXT,
          label: "Type the database name to confirm deletion",
          rules: string()
            .required()
            .test({
              name: "validateConfirmationName",
              message: "Database name does not match",
              test: function (value) {
                return value === database?.name;
              }
            })
        }
      ],
      confirmButtonLabel: "Delete"
    },
    [DIALOG_TYPES.DELETE_REPLICA]: {
      onConfirm: handleConfirmDeleteReplica,
      title: `Are you sure you want to delete replica database?`,
      confirmButtonLabel: "Delete"
    },
    [DIALOG_TYPES.RESTORE_BACKUP]: restoreNeedsDatabase(database?.service_name)
      ? {
          onConfirm: handleRestoreDatabase,
          title: "Select database to restore backup",
          confirmButtonLabel: "Restore",
          fields: [
            {
              name: "database",
              type: FIELD_TYPES.SELECT,
              label: "Restore into",
              helperText: removedBackupSource
                ? `This backup of "${removedBackupSource}" predates instance-named backups. It is restored into the database you pick; "${removedBackupSource}" is not recreated.`
                : undefined,
              options:
                databaseChildren?.map((db) => ({
                  label: db.name,
                  value: db.name
                })) || [],
              rules: selectOptionSchema
            }
          ]
        }
      : {
          // Redis restores the whole keyspace: no database to choose
          onConfirm: handleRestoreDatabase,
          title: "Are you sure you want to restore this backup?",
          confirmButtonLabel: "Restore"
        },
    [DIALOG_TYPES.CREATE_BACKUP]: {
      onConfirm: handleBackupDatabase,
      title: "Create a backup",
      confirmButtonLabel: "Backup",
      fields: supportsPerDatabaseBackups(database?.service_name)
        ? [
            {
              name: "databases",
              type: FIELD_TYPES.MULTISELECT,
              label: "Databases (leave empty for all)",
              // Empty means every database
              options:
                databaseChildren?.map((db) => ({
                  label: db.name,
                  value: db.name
                })) || []
            }
          ]
        : // Redis snapshots the whole keyspace; there is no subset to select, and a
          // multiselect with no options is the empty-form trap that once made every
          // Redis user dialog submit a revoke.
          undefined
    },
    [DIALOG_TYPES.RESTORE_SET]: {
      onConfirm: handleRestoreSet,
      title: "Restore this backup",
      confirmButtonLabel: "Restore",
      fields: [
        ...(restoreNeedsDatabase(database?.service_name)
          ? [
              {
                name: "databases",
                type: FIELD_TYPES.MULTISELECT,
                label: "Databases to restore (leave empty for all)",
                options:
                  rawBackupSets
                    ?.find((set) => set.id === selectedItemId)
                    ?.databases?.map((name) => ({
                      label: name,
                      value: name
                    })) || []
              }
            ]
          : []),
        {
          name: "safetyBackup",
          type: FIELD_TYPES.CHECKBOX,
          // Without this copy a restore cannot be undone
          label:
            database?.service_name === DATABASES.REDIS
              ? "Copy the current data first, so this restore can be undone (undoing it empties the keyspace)"
              : "Copy the current data first, so this restore can be undone",
          defaultValue: true
        }
      ]
    },
    [DIALOG_TYPES.ROLLBACK_RESTORE]: {
      onConfirm: handleRollbackRestore,
      // In the title so the loss is the first thing read
      title:
        database?.service_name === DATABASES.REDIS
          ? "Undo the last restore? The keyspace will be EMPTIED and the copy taken before that restore loaded in its place. Anything written since is lost."
          : "Undo the last restore? The databases go back to the copy taken before it. Anything written since is lost.",
      confirmButtonLabel: "Undo restore"
    },
    [DIALOG_TYPES.DELETE_CRON]: {
      onConfirm: handleCronDeleteBackup,
      title: "Are you sure you want to delete scheduler configuration?",
      confirmButtonLabel: "Delete"
    },
    [DIALOG_TYPES.CREATE_REPLICA]: {
      onConfirm: handleCreateReplicaDatabase,
      title: "Create replica",
      confirmButtonLabel: "Create",
      fields: [
        {
          name: "replProjId",
          type: FIELD_TYPES.SELECT,
          label: "Select project",
          // The API places a replica in the primary's region.
          options: projects
            ?.filter(
              (x) =>
                x.region ===
                (project?.region ??
                  projects.find((p) => p.id === matchParams.projectId)?.region)
            )
            .map((x) =>
              x.id === matchParams.projectId
                ? {
                    label: `${x.name}, ${x.region} (Current project)`,
                    value: x.id
                  }
                : {
                    label: `${x.name}, ${x.region}`,
                    value: x.id
                  }
            ),
          rules: selectOptionSchema
        }
      ]
    },
    [DIALOG_TYPES.RESET_PASSWORD]: {
      onConfirm: () => {
        if (selectedItemId) {
          handleConfirmDatabasePasswordDialog(selectedItemId);
        }
      },
      title: "Are you sure you want to reset password?",
      confirmButtonLabel: "Reset"
    },
    [DIALOG_TYPES.EDIT_CUSTOM_OPTION]: {
      onConfirm: (data: { name: string | SelectOption }) =>
        handleEditDatabaseCustomOptions(data.name),
      title: "Edit custom option",
      confirmButtonLabel: "Save",
      fields: selectedCustomOption
        ? [
            (() => {
              const option = selectedCustomOption;
              const setting: ExtensionSetting = {
                ...customParameterSetting(option),
                description: [
                  option.description,
                  (option.apply_mode === "restart" ||
                    option.restart === "true") &&
                    "Changing this value restarts the database.",
                  option.apply_mode === "live" &&
                    "Applies without restarting once live configuration is enabled."
                ]
                  .filter(Boolean)
                  .join(" ")
              };
              const field = settingField(
                setting,
                option.has_custom_value === "false"
                  ? undefined
                  : option.custom_value
              );
              return {
                ...field,
                name: "name",
                helperText: [
                  typeof field.helperText === "string" ? field.helperText : "",
                  option.rejected_reason || option.pending_reason
                ]
                  .filter(Boolean)
                  .join(" "),
                rules:
                  option.type === "bool" ||
                  option.type === "enum" ||
                  option.type === "timezone"
                    ? option.allow_empty === "true"
                      ? field.rules?.required()
                      : selectOptionSchema.required()
                    : string()
                        .test(
                          "nonempty",
                          "Value is required",
                          (value) => setting.allowEmpty || Boolean(value)
                        )
                        .test(
                          "parameterValue",
                          DATABASE_ERROR_MESSAGES.PARAMETER_VALUE,
                          isParameterValue
                        )
                        .test({
                          name: "parameter",
                          test: (value, context) => {
                            const error = settingValueError(
                              setting,
                              value ?? ""
                            );
                            return error
                              ? context.createError({ message: error })
                              : true;
                          }
                        })
              };
            })()
          ]
        : []
    },
    [DIALOG_TYPES.CREATE_DATABASE_CHILD]: {
      onConfirm: handleCreateDatabaseChild,
      title: "Create Database",
      confirmButtonLabel: "Create",
      fields: [
        {
          name: "name",
          type: FIELD_TYPES.TEXT,
          label: "Database Name",
          rules: childDatabaseNameSchema
        },
        // Charset and collation are SQL-only and required selects
        ...(supportsEncodings(database?.service_name)
          ? [
              {
                name: "charset",
                type: FIELD_TYPES.SELECT,
                label: "Charset",
                defaultValue: toOptionOrUndefined(
                  defaultEncoding(
                    databaseEncodings?.charsets,
                    databaseEncodings?.default_charset
                  )
                ),
                options:
                  databaseEncodings?.charsets?.map((charset) => ({
                    label: charset,
                    value: charset
                  })) || [],
                rules: selectOptionSchema
              },
              {
                name: "collation",
                type: FIELD_TYPES.SELECT,
                label: "Collation",
                defaultValue: toOptionOrUndefined(
                  defaultEncoding(
                    databaseEncodings?.collations,
                    databaseEncodings?.default_collation
                  )
                ),
                options:
                  databaseEncodings?.collations?.map((collation) => ({
                    label: collation,
                    value: collation
                  })) || [],
                rules: selectOptionSchema
              }
            ]
          : []),
        ...(offeredExtensions.length > 0
          ? [
              {
                name: "extensions",
                type: FIELD_TYPES.MULTISELECT,
                label: "Extensions",
                options: extensionOptions(offeredExtensions, {
                  host: extensionTarget
                }),
                rules: array(selectOptionSchema)
              },
              {
                name: "extensionsInfo",
                type: FIELD_TYPES.LABEL,
                label: extensionsNote(
                  offeredExtensions,
                  extensionTarget,
                  extensionsOnInstance
                )
              },
              ...pickedExtensionSettingsFields(offeredExtensions)
            ]
          : [])
      ]
    },
    [DIALOG_TYPES.EDIT_DATABASE_CHILD_EXTENSIONS]: {
      onConfirm: handleUpdateDatabaseChildExtensions,
      title: `Extensions of "${selectedItemId ?? "selected"}" database`,
      confirmButtonLabel: "Save",
      fields: [
        {
          name: "extensions",
          type: FIELD_TYPES.MULTISELECT,
          label: "Extensions",
          options: editExtensionOptions,
          defaultValue: editExtensionOptions.filter((option) =>
            selectedChildExtensions.includes(option.value)
          ),
          rules: array(selectOptionSchema)
        },
        {
          name: "extensionsInfo",
          type: FIELD_TYPES.LABEL,
          label: extensionsNote(
            offeredExtensions,
            extensionTarget,
            extensionsOnInstance
          )
        }
      ]
    },
    [DIALOG_TYPES.EDIT_DATABASE_CHILD_SETTINGS]: {
      onConfirm: handleUpdateDatabaseChildSettings,
      title: `Settings of "${selectedItemId ?? "selected"}" database`,
      confirmButtonLabel: "Save",
      fields: [
        ...extensionSettingsFields(
          offeredExtensions,
          selectedChildExtensions,
          selectedChild?.settings
        ),
        {
          name: "settingsInfo",
          type: FIELD_TYPES.NOTES,
          label: SETTINGS_NOTE
        }
      ]
    },
    [DIALOG_TYPES.DELETE_DATABASE_CHILD]: {
      onConfirm: handleConfirmDeleteDatabaseChild,
      title: `Are you sure you want to delete "${
        selectedItemId ?? "selected"
      }" database?`,
      fields: [
        {
          name: "confirmationName",
          type: FIELD_TYPES.TEXT,
          label: "Type the database name to confirm deletion",
          rules: string()
            .required()
            .test({
              name: "validateConfirmationName",
              message: "Database name does not match",
              test: function (value) {
                return value === selectedItemId;
              }
            })
        }
      ],
      confirmButtonLabel: "Delete"
    },
    [DIALOG_TYPES.CREATE_DATABASE_USER]: {
      onConfirm: (data: {
        name: string;
        databases: SelectOption[];
        [key: string]: unknown;
      }) => {
        const permissions = grantsPerDatabase
          ? data.databases.map((database) => ({
              database: database.value,
              permission: readPermissionLevel(
                data[`permission_${database.value}`]
              )
            }))
          : [
              {
                database: SERVER_WIDE_GRANT_SCOPE,
                permission: readPermissionLevel(data[SERVER_WIDE_FIELD])
              }
            ];

        const formattedData = {
          name: data.name,
          permissions
        };
        handleCreateDatabaseUser(formattedData);
      },
      title: "Create User",
      confirmButtonLabel: "Create",
      fields: [
        {
          name: "name",
          type: FIELD_TYPES.TEXT,
          label: "Username",
          rules: userNameSchema
        },
        ...(grantsPerDatabase
          ? [
              {
                name: "databases",
                type: FIELD_TYPES.MULTISELECT,
                label: "Databases",
                options:
                  databaseChildren?.map((db) => ({
                    label: db.name,
                    value: db.name
                  })) || [],
                rules: array(selectOptionSchema)
              },
              {
                name: "permissionsInfo",
                type: FIELD_TYPES.NOTES,
                isHidden: (fieldValues: { databases?: SelectOption[] }) => {
                  return !(
                    fieldValues.databases && fieldValues.databases.length === 0
                  );
                },
                label: "ℹ️ Select databases to set permissions"
              },
              ...(databaseChildren?.map((db) => ({
                name: `permission_${db.name}`,
                type: FIELD_TYPES.SELECT,
                label: `Permission for ${db.name}`,
                options: PERMISSION_OPTIONS,
                defaultValue: { label: "Read/Write", value: "RW" },
                isHidden: (fieldValues: {
                  databases?: { value: string }[];
                }) => {
                  const selectedDatabases = fieldValues.databases || [];
                  return !selectedDatabases.some(
                    (selected) => selected.value === db.name
                  );
                }
              })) || [])
            ]
          : [
              {
                name: SERVER_WIDE_FIELD,
                type: FIELD_TYPES.SELECT,
                label: "Permission",
                options: PERMISSION_OPTIONS,
                defaultValue: { label: "Read/Write", value: "RW" }
              },
              {
                name: "permissionsInfo",
                type: FIELD_TYPES.NOTES,
                label: SERVER_WIDE_GRANT_NOTE
              }
            ])
      ]
    },
    [DIALOG_TYPES.DELETE_DATABASE_USER]: {
      onConfirm: handleConfirmDeleteDatabaseUser,
      title: `Are you sure you want to delete "${
        selectedItemId ?? "selected"
      }" user?`,
      fields: [
        {
          name: "confirmationName",
          type: FIELD_TYPES.TEXT,
          label: "Type the user name to confirm deletion",
          rules: string()
            .required()
            .test({
              name: "validateConfirmationName",
              message: "User name does not match",
              test: function (value) {
                return value === selectedItemId;
              }
            })
        }
      ]
    },
    [DIALOG_TYPES.SET_PERMISSIONS]: {
      onConfirm: (data: {
        selectedDatabases: SelectOption[];
        [key: string]: unknown;
      }) => {
        const permissions: DatabasePermission[] = grantsPerDatabase
          ? data.selectedDatabases.map((database) => ({
              database: database.value,
              permission: readPermissionLevel(
                data[`permission_${database.value}`]
              )
            }))
          : [
              {
                database: SERVER_WIDE_GRANT_SCOPE,
                permission: readPermissionLevel(data[SERVER_WIDE_FIELD])
              }
            ];

        if (selectedItemId) {
          handleSetUserPermissions(selectedItemId, permissions);
        }
      },
      title: `Set User Permissions for ${selectedItemId || "User"}`,
      confirmButtonLabel: "Set Permissions",
      fields: grantsPerDatabase
        ? [
            {
              name: "selectedDatabases",
              type: FIELD_TYPES.MULTISELECT,
              label: "Select Databases",
              options:
                databaseChildren?.map((db) => ({
                  label: db.name,
                  value: db.name
                })) || [],
              defaultValue: initializePermissions(selectedItemId!).map(
                (perm) => ({
                  label: perm.database,
                  value: perm.database
                })
              ),
              rules: array(selectOptionSchema)
            },
            {
              name: "permissionsInfo",
              type: FIELD_TYPES.NOTES,
              isHidden: (fieldValues: {
                selectedDatabases?: SelectOption[];
              }) => {
                return !(
                  fieldValues.selectedDatabases &&
                  fieldValues.selectedDatabases.length === 0
                );
              },
              label: "ℹ️ Select databases to set permissions"
            },
            ...(databaseChildren?.map((db) => ({
              name: `permission_${db.name}`,
              type: FIELD_TYPES.SELECT,
              label: `Permission for ${db.name}`,
              options: PERMISSION_OPTIONS,
              defaultValue: {
                label:
                  initializePermissions(selectedItemId!).find(
                    (perm) => perm.database === db.name
                  )?.permission === "RW"
                    ? "Read/Write"
                    : "Read",
                value:
                  initializePermissions(selectedItemId!).find(
                    (perm) => perm.database === db.name
                  )?.permission || "R"
              },
              isHidden: (fieldValues: {
                selectedDatabases?: { value: string }[];
              }) => {
                const selectedDatabases = fieldValues.selectedDatabases || [];
                return !selectedDatabases.some(
                  (selected) => selected.value === db.name
                );
              }
            })) || [])
          ]
        : [
            {
              name: SERVER_WIDE_FIELD,
              type: FIELD_TYPES.SELECT,
              label: "Permission",
              options: PERMISSION_OPTIONS,
              // A server-wide grant is reported against every logical database
              // with the same level, so the first entry is the current one.
              defaultValue:
                initializePermissions(selectedItemId!)[0]?.permission === "RW"
                  ? { label: "Read/Write", value: "RW" }
                  : { label: "Read", value: "R" }
            },
            {
              name: "permissionsInfo",
              type: FIELD_TYPES.NOTES,
              label: SERVER_WIDE_GRANT_NOTE
            }
          ]
    }
  };

  const generateReplicaTableItemURL = useCallback(
    (id: string) => {
      const replicaRegionId = replicaDatabases?.find(
        (database) => database.id === id
      )?.region;
      const replicaProjectId = replicaDatabases?.find(
        (database) => database.id === id
      )?.project_id;
      const replicaServiceName = replicaDatabases
        ?.find((database) => database.id === id)
        ?.serviceName.toLowerCase();

      if (replicaRegionId && replicaProjectId && replicaServiceName) {
        return generatePath(ROUTES.DATABASE, {
          organizationId: matchParams.organizationId,
          regionId: replicaRegionId,
          projectId: replicaProjectId,
          serviceName: replicaServiceName,
          databaseId: id
        });
      }
    },
    [matchParams.organizationId, replicaDatabases]
  );

  const title = database?.name;

  const tabContent = [
    <Fragment key={"databaseChildrenTab"}>
      <Table<TableDatabaseChild>
        rows={databaseChildren || []}
        isSearchEnabled={true}
        isSortingEnabled={true}
        columns={databaseChildrenTableColumnsFor(
          database?.service_name,
          hasExtensions,
          Boolean(databaseChildren?.some((db) => db.notes))
        )}
        actions={
          databaseChildrenTableActions.length > 0
            ? databaseChildrenTableActions
            : undefined
        }
        isLoading={!databaseChildren}
        toolbarItems={
          areDatabasesEditable ? (
            <span>
              <Button
                onClick={() =>
                  setDialog({
                    type: DIALOG_TYPES.CREATE_DATABASE_CHILD,
                    isOpened: true
                  })
                }
                variant="contained"
              >
                Create Database
              </Button>
            </span>
          ) : undefined
        }
      />
    </Fragment>,
    <Fragment key={"databaseUsersTab"}>
      <Table<TableDatabaseUser>
        rows={databaseUsers || []}
        isSearchEnabled={true}
        isSortingEnabled={true}
        columns={databaseUsersTableColumns}
        actions={
          databaseUsersTableActions.length > 0
            ? databaseUsersTableActions
            : undefined
        }
        isLoading={!databaseUsers}
        toolbarItems={
          areAccountsEditable ? (
            <span>
              <Button
                onClick={() =>
                  setDialog({
                    type: DIALOG_TYPES.CREATE_DATABASE_USER,
                    isOpened: true
                  })
                }
                variant="contained"
              >
                Create User
              </Button>
            </span>
          ) : undefined
        }
      />
    </Fragment>,
    <Table<TableReplicaDatabase>
      key={"replicaTable"}
      isSearchEnabled={true}
      isSortingEnabled={true}
      rows={replicaDatabases || []}
      columns={replicasTableColumns}
      actions={replicaTableActions}
      // Spinner until the first load only, not on every poll
      isLoading={!replicaDatabases}
      toolbarItems={
        <Tooltip
          arrow
          title={
            !isCreateReplicaEnabled ? (
              <>
                Database must be in ACTIVE state. Database must be not a
                replica. Count of replicas must be less than{" "}
                {MAX_DATABASE_REPLICAS_COUNT}.
              </>
            ) : (
              ""
            )
          }
        >
          <span>
            <>
              <Button
                onClick={handleCreateReplicaButtonClick}
                variant={"contained"}
                disabled={!isCreateReplicaEnabled}
              >
                Create Replica
              </Button>
            </>
          </span>
        </Tooltip>
      }
      itemLink={{
        column: "name",
        getURL: generateReplicaTableItemURL
      }}
    />,
    <DatabaseBackups
      key="backupsTab"
      status={backupStatus}
      sets={backupSets}
      files={backupFiles}
      setActions={backupSetsTableActions}
      fileActions={backupsTableActions}
      operationsDisabled={areBackupOrRestoreDisabled}
      disabledReason={
        !isServing(database?.service_status)
          ? "Start the database before running a backup or restore."
          : "A database backup or restore is already in progress."
      }
      changingStorageDisabled={backupOperationInProgress}
      storageBusy={storageLinking || storageUnlinking}
      busy={isCronBackupUpdating || isCronBackupDeleting}
      listingError={backupFilesError}
      onCreate={handleCreateBackupButtonClick}
      onUndoRestore={handleRollbackButtonClick}
      onConfigure={handleCronButtonClick}
      onRemove={handleCronDeleteButtonClick}
      onConnect={handleS3LinkButtonClick}
      onDisconnect={handleS3UnlinkDatabaseButtonClick}
      onRefresh={() =>
        dispatch(
          databasesActions.getDatabaseBackupFilesRequest({
            orgId: matchParams.organizationId!,
            projId: matchParams.projectId!,
            id: matchParams.databaseId!,
            serviceName: matchParams.serviceName!
          })
        )
      }
    />,
    <Table<TableDatabaseCustomOptions>
      key={"customOptionsTable"}
      isSearchEnabled={true}
      isSortingEnabled={true}
      rows={databaseCustomOptions || []}
      columns={customOptionsTableColumns}
      actions={customOptionsTableActions}
      isLoading={!databaseCustomOptions}
      itemWithIcon={{
        withText: false,
        column: "custom_value",
        getIcon: (row) =>
          row.rejected_reason !== undefined ? (
            <s.RejectedParameterValue>
              {row.custom_value}
            </s.RejectedParameterValue>
          ) : row.state === "pending" ? (
            <s.PendingParameterValue>
              {row.custom_value}
            </s.PendingParameterValue>
          ) : (
            <s.AppliedParameterValue>
              {row.custom_value}
            </s.AppliedParameterValue>
          ),
        tooltipText: (row) =>
          row.rejected_reason !== undefined
            ? `Not applied to the engine — ${row.rejected_reason}`
            : row.pending_reason
              ? `Pending — ${row.pending_reason}`
              : ""
      }}
    />
  ];

  const metricsLoad = databaseMetrics?.hw_load?.load;

  const endDateMetric =
    metricsLoad && metricsLoad.length > 0
      ? metricsLoad[metricsLoad.length - 1].time_stamp
      : new Date().valueOf();

  const startDateMetric =
    endDateMetric - (databaseMetrics?.hw_load?.range_minutes || 60) * 60 * 1000;

  const volumeCapacityUsed = databaseMetrics?.volume_capacity_used;

  const cronTimezone = backupStatus?.cron_timezone;
  const cronNextRun = backupStatus?.cron_next_run;

  const domain = () => [startDateMetric, endDateMetric];
  const argumentFormat = () => (tick: string) =>
    `${formatDate(new Date(tick), DATE_FORMATS.TIME)}`;
  const valueFormat = () => (tick: string) => `${tick} %`;

  const notices = sortedNotices([
    database?.upgrade?.phase &&
      database.upgrade.phase !== "Completed" && {
        severity:
          database.upgrade.phase === "Failed" ||
          database.upgrade.phase === "Blocked"
            ? "error"
            : "info",
        title: `Upgrading ${database.upgrade.from_version} → ${database.upgrade.to_version}`,
        message: database.upgrade.message || database.upgrade.phase
      },
    migration,
    Boolean(database?.rejected_permissions?.length) && {
      severity: "warning",
      title: "Permissions not applied",
      message: database?.rejected_permissions?.join("; ")
    },
    ...REJECTED_ALERTS.map(
      ({ key, title }): Notice | false =>
        Boolean(database?.[key]?.length) && {
          severity: "warning",
          title,
          message: database?.[key]?.join("; ")
        }
    ),
    Boolean(database?.pending_extensions?.length) && {
      severity: "info",
      title: "Extensions installing after the database restarts",
      message: database?.pending_extensions?.join("; ")
    },
    accountsReason && { severity: "info", message: accountsReason },
    database?.load_balancer_message && {
      severity: "error",
      title: "Load balancer not applied",
      message: database.load_balancer_message
    },
    backupStatus?.backup_lastrun_message &&
      backupStatus.backup_lastrun_result !== "Success" && {
        severity: "error",
        title: "Last backup failed",
        message: backupStatus.backup_lastrun_message
      },
    backupStatus?.link_exist &&
      backupStatus.backup_status &&
      backupStatus.backup_status !== DATABASE_BACKUP_STATUSES.COMPLETED && {
        severity: "info",
        title: "Backup in progress",
        message:
          "New backups, restores and storage changes are unavailable until it finishes."
      },
    (backupStatus?.restore_phase === DATABASE_RESTORE_PHASES.FAILED ||
      backupStatus?.restore_phase === DATABASE_RESTORE_PHASES.ROLLED_BACK) && {
      severity:
        backupStatus.restore_phase === DATABASE_RESTORE_PHASES.FAILED
          ? "error"
          : "warning",
      title:
        backupStatus.restore_phase === DATABASE_RESTORE_PHASES.FAILED
          ? "Restore failed"
          : "Restore failed and was rolled back",
      message:
        backupStatus.restore_message || backupStatus.restore_lastrun_message
    }
  ]);

  return (
    <>
      <Head title={title} />
      {database ? (
        <>
          {organization && project && <Breadcrumbs breadcrumbs={breadcrumbs} />}
          {notices.length === 1 && (
            <Alert severity={notices[0].severity} sx={{ mb: 2 }}>
              {notices[0].title && <AlertTitle>{notices[0].title}</AlertTitle>}
              {notices[0].message}
            </Alert>
          )}
          {notices.length > 1 && (
            <Alert severity={notices[0].severity} sx={{ mb: 2 }}>
              <AlertTitle>{notices.length} notices</AlertTitle>
              <s.NoticeList>
                {notices.map((notice, i) => {
                  const Icon = NOTICE_ICONS[notice.severity];
                  return (
                    <s.NoticeItem key={i}>
                      <Icon fontSize="small" color={notice.severity} />
                      <span>
                        {notice.title && <strong>{notice.title}: </strong>}
                        {notice.message}
                      </span>
                    </s.NoticeItem>
                  );
                })}
              </s.NoticeList>
            </Alert>
          )}
          <>
            <s.SummaryContainer>
              <s.TitleContainer>
                <s.Title title={database?.name} variant="h4" component="h2">
                  {database?.name}
                </s.Title>
              </s.TitleContainer>
              <s.ActionsContainer>
                <Tooltip title={"Start"} arrow>
                  <span>
                    <IconButton
                      disabled={
                        database.service_status !== DATABASE_STATUSES.STOPPED
                      }
                      onClick={handleChangeDatabaseStatusButtonClick(
                        CHANGE_STATUS_TYPES.START
                      )}
                      color={"inherit"}
                    >
                      <PlayArrowIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={"Stop"} arrow>
                  <span>
                    <IconButton
                      disabled={!isServing(database.service_status)}
                      onClick={handleChangeDatabaseStatusButtonClick(
                        CHANGE_STATUS_TYPES.STOP
                      )}
                      color={"inherit"}
                    >
                      <StopIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Database management" arrow>
                  <IconButton
                    onClick={handleMgrLinkDatabaseButtonClick}
                    color="inherit"
                  >
                    <LaunchIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip
                  title={
                    areAccountsEditable ? "Reset password" : accountsReason
                  }
                  arrow
                >
                  <span>
                    <IconButton
                      disabled={!isInstancePasswordResettable}
                      onClick={() =>
                        setIsResetDefaultPasswordDialogOpened(true)
                      }
                      color="inherit"
                    >
                      <LockResetIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Edit" arrow>
                  <IconButton
                    onClick={handleEditDatabaseButtonClick}
                    color="inherit"
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete" arrow>
                  <IconButton
                    onClick={handleDeleteDatabaseButtonClick}
                    color="inherit"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
                <Menu
                  isOpened={isActionsMenuOpened}
                  onClose={handleActionsMenuClose}
                  anchorEl={actionsMenuButtonRef.current}
                />
              </s.ActionsContainer>
            </s.SummaryContainer>

            <s.InfoContainer variant="outlined">
              <Box
                sx={{
                  display: "grid",
                  // Columns reflow by how many fit; four sections need 1008px for one row
                  gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                  gap: 2
                }}
              >
                <Box>
                  <s.SectionTitle>General</s.SectionTitle>
                  <s.InfoItem>
                    <s.InfoLabel>ID:</s.InfoLabel>
                    <s.InfoValue>{database?.id}</s.InfoValue>
                  </s.InfoItem>
                  <s.InfoItem>
                    <s.InfoLabel>Created:</s.InfoLabel>
                    <s.InfoValue>{database?.ageString}</s.InfoValue>
                  </s.InfoItem>
                  <s.InfoItem>
                    <s.InfoLabel>Status:</s.InfoLabel>
                    {database?.service_status ===
                    DATABASE_STATUSES.UPGRADING ? (
                      <Chip size="small" color="warning" label="Upgrading" />
                    ) : database?.service_status ===
                      DATABASE_STATUSES.DEGRADED ? (
                      <Chip size="small" color="warning" label="Degraded" />
                    ) : database?.service_status ===
                      DATABASE_STATUSES.FAILED ? (
                      <Chip size="small" color="error" label="Failed" />
                    ) : (
                      <s.Tag label={database?.service_status} />
                    )}
                  </s.InfoItem>
                  <s.InfoItem>
                    <s.InfoLabel>Mode:</s.InfoLabel>
                    <s.Tag label={database?.mode} />
                  </s.InfoItem>
                  {replicaDatabases &&
                    database?.mode === DATABASE_REPLICA_STATUSES.PRIMARY && (
                      <s.InfoItem>
                        <s.InfoLabel>Replicas:</s.InfoLabel>
                        <s.InfoValue>
                          {replicaDatabases?.length}/
                          {MAX_DATABASE_REPLICAS_COUNT}
                        </s.InfoValue>
                      </s.InfoItem>
                    )}

                  {/* HA is shown whether on or off, and gated on high_availability.enabled: replica_status outlives an HA teardown */}
                  <s.InfoItem>
                    <s.InfoLabel>High availability:</s.InfoLabel>
                    <s.Tag
                      label={
                        database?.high_availability?.enabled ? "Yes" : "No"
                      }
                    />
                  </s.InfoItem>
                  {database?.high_availability?.enabled && (
                    <>
                      {/* State only: member names and replication positions are internal and change on failover */}
                      {database.replica_status?.phase && (
                        <s.InfoItem>
                          <s.InfoLabel>Health:</s.InfoLabel>
                          <Chip
                            size="small"
                            color={getHAPhaseChipColor(
                              database.replica_status.phase
                            )}
                            label={database.replica_status.phase}
                          />
                        </s.InfoItem>
                      )}
                      {isNumber(
                        database.high_availability.failover_delay_seconds
                      ) && (
                        <s.InfoItem>
                          <s.InfoLabel>Failover delay:</s.InfoLabel>
                          <s.InfoValue>
                            {`${database.high_availability.failover_delay_seconds}s`}
                          </s.InfoValue>
                        </s.InfoItem>
                      )}
                      {database.replica_status?.last_failover_time && (
                        <s.InfoItem>
                          <s.InfoLabel>Last failover:</s.InfoLabel>
                          <s.InfoValue>
                            {formatDate(
                              new Date(
                                database.replica_status.last_failover_time *
                                  1000
                              ),
                              DATE_FORMATS.DATETIME
                            )}
                          </s.InfoValue>
                        </s.InfoItem>
                      )}
                    </>
                  )}
                  {database?.pooler?.enabled && (
                    <>
                      <s.InfoItem>
                        <s.InfoLabel>Connection pooler:</s.InfoLabel>
                        <s.Tag label="Yes" />
                      </s.InfoItem>
                      <s.InfoItem>
                        <s.InfoLabel>Pooler endpoint:</s.InfoLabel>
                        <s.InfoValue>
                          {database.pooler.ready && database.pooler.host
                            ? `${database.pooler.host}:${database.pooler.port}`
                            : database.pooler.message || "Provisioning"}
                        </s.InfoValue>
                      </s.InfoItem>
                      <s.InfoItem>
                        <s.InfoLabel>Pooling mode:</s.InfoLabel>
                        <s.InfoValue>{database.pooler.mode}</s.InfoValue>
                      </s.InfoItem>
                    </>
                  )}
                </Box>

                <Box>
                  <s.SectionTitle>Resources</s.SectionTitle>
                  <s.InfoItem>
                    <s.InfoLabel>Version:</s.InfoLabel>
                    <s.Tag label={database?.versionString} />
                  </s.InfoItem>
                  {database?.observed_version &&
                    database.observed_version !== database.version && (
                      <s.InfoItem>
                        <s.InfoLabel>Running:</s.InfoLabel>
                        <s.Tag label={database.observed_version} />
                      </s.InfoItem>
                    )}
                  <s.InfoItem>
                    <s.InfoLabel>vCPU:</s.InfoLabel>
                    <s.Tag label={database?.cpuString} />
                  </s.InfoItem>
                  <s.InfoItem>
                    <s.InfoLabel>Memory:</s.InfoLabel>
                    <s.Tag label={database?.memoryString} />
                  </s.InfoItem>
                  <s.InfoItem>
                    <s.InfoLabel>Disk:</s.InfoLabel>
                    <s.Tag
                      label={`${database?.diskString} ${
                        isNumber(volumeCapacityUsed)
                          ? ` (${roundTo(volumeCapacityUsed, 2)}%)`
                          : ""
                      }`}
                    />
                  </s.InfoItem>
                  {database?.high_availability?.enabled && (
                    <s.InfoItem>
                      {/* The figures above are per member; billing owns the arithmetic, so only the multiplier is shown */}
                      <s.InfoLabel>Charged:</s.InfoLabel>
                      <Tooltip
                        title={`High availability runs ${haMemberCount(
                          database?.service_name
                        )} members. CPU, memory and disk are charged per member.`}
                      >
                        <span>
                          <s.Tag
                            label={`x${haMemberCount(
                              database?.service_name
                            )} (HA)`}
                          />
                        </span>
                      </Tooltip>
                    </s.InfoItem>
                  )}
                </Box>

                <Box>
                  <s.SectionTitle>Backup</s.SectionTitle>
                  <s.InfoItem>
                    <s.InfoLabel>Backing up:</s.InfoLabel>
                    <s.Tag label={backupStatus?.backup_status || "idle"} />
                  </s.InfoItem>
                  <s.InfoItem>
                    <s.InfoLabel>Restoring:</s.InfoLabel>
                    <s.Tag label={backupStatus?.restore_status || "idle"} />
                  </s.InfoItem>
                  {backupStatus?.restore_phase && (
                    <s.InfoItem>
                      <s.InfoLabel>Restore phase:</s.InfoLabel>
                      <Tooltip title={backupStatus.restore_message || ""} arrow>
                        <s.Tag label={backupStatus.restore_phase} />
                      </Tooltip>
                    </s.InfoItem>
                  )}
                  {/* The outcome of the last backup, so a failing schedule is visible */}
                  <s.InfoItem>
                    <s.InfoLabel>Last backup:</s.InfoLabel>
                    {backupStatus?.backup_lastrun_result ? (
                      <Tooltip
                        title={backupStatus.backup_lastrun_message || ""}
                        arrow
                      >
                        <Chip
                          size="small"
                          color={
                            backupStatus.backup_lastrun_result === "Success"
                              ? "success"
                              : "error"
                          }
                          label={backupStatus.backup_lastrun_result}
                        />
                      </Tooltip>
                    ) : (
                      <s.Tag label={"never"} />
                    )}
                  </s.InfoItem>
                  {isNumber(backupStatus?.backup_lastrun_date) && (
                    <s.InfoItem>
                      <s.InfoLabel>Last run:</s.InfoLabel>
                      <s.InfoValue>
                        {formatDate(
                          new Date(
                            Number(backupStatus?.backup_lastrun_date) * 1000
                          ),
                          DATE_FORMATS.DATETIME
                        )}
                      </s.InfoValue>
                    </s.InfoItem>
                  )}
                  {cronTimezone && (
                    <s.InfoItem>
                      <s.InfoLabel>Timezone:</s.InfoLabel>
                      <s.InfoValue>{cronTimezone}</s.InfoValue>
                    </s.InfoItem>
                  )}
                  {isNumber(cronNextRun) && (
                    <s.InfoItem>
                      <s.InfoLabel>Next run:</s.InfoLabel>
                      <s.InfoValue>
                        {formatDate(
                          new Date(cronNextRun * 1000),
                          DATE_FORMATS.DATETIME
                        )}
                      </s.InfoValue>
                    </s.InfoItem>
                  )}
                  <s.InfoItem>
                    <s.InfoLabel>S3:</s.InfoLabel>
                    <s.Tag
                      label={backupStatus?.link_exist ? "Linked" : "Unlinked"}
                    />
                  </s.InfoItem>
                </Box>

                <Box>
                  <s.SectionTitle>Connection information</s.SectionTitle>
                  <s.InfoItem>
                    <s.InfoLabel>Host:</s.InfoLabel>
                    <Tooltip
                      title={copySuccess ? "Copied!" : "Click to copy"}
                      arrow
                    >
                      <s.CopyableInfoValue onClick={handleCopyConnectionString}>
                        {database?.connectionString}
                        <s.CopyIcon>
                          <ContentCopyIcon fontSize="small" />
                        </s.CopyIcon>
                      </s.CopyableInfoValue>
                    </Tooltip>
                  </s.InfoItem>
                  {backupStatus?.link_exist && (
                    <>
                      <s.InfoItem>
                        <s.InfoLabel>S3 Endpoint:</s.InfoLabel>
                        <s.InfoValue>{backupStatus?.s3endpoint}</s.InfoValue>
                      </s.InfoItem>
                      <s.InfoItem>
                        <s.InfoLabel>S3 Key:</s.InfoLabel>
                        <s.Tag
                          icon={<LockOutlinedIcon fontSize="small" />}
                          label="Configured"
                        />
                      </s.InfoItem>
                    </>
                  )}
                </Box>
              </Box>
            </s.InfoContainer>
          </>
          <s.MetricsContainer variant={"outlined"}>
            <Chart height={250} data={databaseMetrics?.hw_load?.load || []}>
              <Title text={"Usage"} />
              <ArgumentScale factory={scaleTime} modifyDomain={domain} />
              <ValueAxis
                showLine={true}
                showTicks={true}
                tickFormat={valueFormat}
              />
              <ArgumentAxis showLine={true} tickFormat={argumentFormat} />
              <LineSeries
                name={"Memory"}
                valueField={"memory_value"}
                argumentField={"time_stamp"}
              />
              <LineSeries
                name={"CPU"}
                valueField={"cpu_value"}
                argumentField={"time_stamp"}
              />
              <EventTracker />
              <DXTooltip />
              <Legend />
            </Chart>
          </s.MetricsContainer>
          <Tabs value={activeTabIndex} onChange={handleChangeTab}>
            {Object.entries(TAB_TITLES)
              .filter(([tabIndex]) => isTabVisible(Number(tabIndex)))
              .map(([tabIndex, tabTitle]) => (
                <Tab key={tabTitle} value={Number(tabIndex)} label={tabTitle} />
              ))}
          </Tabs>
          {activeTabIndex === TABS.NETWORK ? (
            <LoadBalancers database={database} />
          ) : (
            tabContent[activeTabIndex]
          )}
          <FormDialog
            isOpened={dialog.isOpened}
            onCancel={handleCloseDialog}
            fields={dialogProps[dialog.type].fields}
            onConfirm={dialogProps[dialog.type].onConfirm}
            title={dialogProps[dialog.type].title}
            confirmButtonLabel={dialogProps[dialog.type].confirmButtonLabel}
            isLoading={dialogProps[dialog.type].isLoading}
          />
          <FormDialog
            isOpened={isResetDefaultPasswordDialogOpened}
            onCancel={() => setIsResetDefaultPasswordDialogOpened(false)}
            onConfirm={handleResetDefaultUserPassword}
            title={`Reset password for user "${database?.user ?? ""}"?`}
            confirmButtonLabel="Reset"
          />
          <BackupScheduleDialog
            isOpened={isScheduleOpened}
            status={backupStatus}
            onCancel={() => setScheduleOpened(false)}
            onConfirm={handleCronBackupApply}
            isSaving={isCronBackupUpdating}
            error={scheduleResult.error}
          />
          <S3LinkDialog
            key={matchParams.databaseId}
            isOpened={isStorageLinkOpened}
            regionId={matchParams.regionId!}
            projectId={matchParams.projectId!}
            onCancel={() => setStorageLinkOpened(false)}
            onConfirm={handleS3LinkDatabase}
          />
          {databaseResetedPassword &&
            isString(databaseResetedPassword.password) && (
              <DatabasePasswordDialog
                isOpened={isDatabasePasswordDialogOpened}
                name={databaseResetedPassword.user || database.user}
                text={databasePasswordTextAfterReset}
                password={databaseResetedPassword.password}
                onClose={handleCloseDatabasePasswordDialog}
              />
            )}
          {databaseUser?.password && (
            <DatabasePasswordDialog
              isOpened={isDatabasePasswordDialogOpened}
              name={databaseUser.user}
              text={databasePasswordTextAfterReset}
              password={databaseUser.password}
              onClose={handleCloseDatabasePasswordDialog}
            />
          )}
        </>
      ) : (
        <Loader text={"Loading data..."} />
      )}
    </>
  );
};
